import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { myOrdersApi, companiesApi, directorsApi, paymentsApi, adminApi, ordersApi, recurringPauseApi, dscConsentApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ErrorBoundary from '../components/ErrorBoundary';
import { KYCDocuments } from '../components/KYCDocuments';

// Safe error message extraction — prevents React crash from rendering Pydantic error objects
const getApiError = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || 'Validation error').join(', ');
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};
import { 
  ArrowLeft, Loader2, Upload, FileText, CheckCircle, 
  Clock, Download, ChevronDown, ChevronUp, Pencil, Save, X, User, 
  Phone, Mail, Plus, UserPlus, Send, FileCheck, PenTool, Eye, CreditCard, IndianRupee,
  CalendarClock, AlertTriangle, Building2, Tag, DollarSign, AlertCircle,
  MessageSquare, Activity, XCircle, Lock, PauseCircle, PlayCircle, Trash2, Shield, LifeBuoy
} from 'lucide-react';
import { toast } from 'sonner';
import { toIST } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Currency helper - India only platform, always INR
const getCurrencyInfo = () => {
  return {
    symbol: '₹',
    code: 'INR',
    locale: 'en-IN',
  };
};

// Format currency amount
const formatCurrency = (amount, currencyInfo) => {
  if (!amount && amount !== 0) return '-';
  return `${currencyInfo.symbol}${Number(amount).toLocaleString(currencyInfo.locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const getFileUrl = (fileUrl) => {
  const token = localStorage.getItem('token');
  if (!fileUrl) return '';
  return `${API_URL}${fileUrl}${fileUrl.includes('?') ? '&' : '?'}token=${token}`;
};

// Deadline Display Component
const DeadlineCard = ({ label, date, icon: Icon, priority, description }) => {
  if (!date) return null;
  
  const deadlineDate = new Date(date);
  const today = new Date();
  const daysRemaining = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
  
  const isOverdue = daysRemaining < 0;
  const isUrgent = daysRemaining >= 0 && daysRemaining <= 3;
  const isWarning = daysRemaining > 3 && daysRemaining <= 7;
  
  const getBorderColor = () => {
    if (isOverdue) return 'border-red-500 bg-red-500/10';
    if (isUrgent) return 'border-red-400 bg-red-500/5';
    if (isWarning) return 'border-amber-400 bg-amber-500/5';
    return 'border-emerald-800/40 bg-[#152219]/50';
  };
  
  const getTextColor = () => {
    if (isOverdue) return 'text-red-400';
    if (isUrgent) return 'text-red-400';
    if (isWarning) return 'text-amber-400';
    return 'text-slate-300';
  };
  
  return (
    <div className={`rounded-lg border p-3 ${getBorderColor()}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${getTextColor()}`} />}
          <span className="text-sm font-medium text-white">{label}</span>
        </div>
        {(isOverdue || isUrgent) && (
          <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse" />
        )}
      </div>
      <p className={`text-lg font-bold mt-1 ${getTextColor()}`}>
        {deadlineDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
      </p>
      <p className={`text-xs mt-1 ${isOverdue ? 'text-red-300' : isUrgent ? 'text-red-300' : 'text-slate-400'}`}>
        {isOverdue 
          ? `Overdue by ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''}` 
          : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`}
      </p>
      {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
    </div>
  );
};

// Last Updated Badge Component for client side
const LastUpdatedBadge = ({ updatedAt, updatedByName, updatedByRole }) => {
  if (!updatedAt) return null;
  
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  const getRoleBadge = (role) => {
    if (role === 'super_admin' || role === 'admin') {
      return { text: 'Admin', color: 'bg-purple-500/20 text-purple-400' };
    }
    return { text: 'You', color: 'bg-blue-500/20 text-blue-400' };
  };
  
  const roleBadge = getRoleBadge(updatedByRole);
  
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
      <Clock className="w-3.5 h-3.5" />
      <span>Updated {formatDate(updatedAt)}</span>
      {updatedByName && (
        <>
          <span>by</span>
          <span className={`px-2 py-0.5 rounded-full ${roleBadge.color}`}>
            {roleBadge.text}
          </span>
        </>
      )}
    </div>
  );
};

// Simplified flow: Start > Invoice > Payment > Documents > Acknowledgement
const PROGRESS_STEPS = [
  { key: 'draft', label: 'Draft', statuses: ['draft'], percent: 10 },
  { key: 'invoice_requested', label: 'Invoice Requested', statuses: ['invoice_requested'], percent: 15 },
  { key: 'payment_pending', label: 'Payment Pending', statuses: ['payment_pending'], percent: 25 },
  { key: 'payment_received', label: 'Payment Received', statuses: ['payment_received'], percent: 35 },
  { key: 'documents_pending', label: 'Documents Pending', statuses: ['documents_pending', 'pending_documents'], percent: 45 },
  { key: 'under_review', label: 'Under Review', statuses: ['under_review'], percent: 55 },
  { key: 'signature_pending', label: 'Signature Pending', statuses: ['signature_pending'], percent: 65 },
  { key: 'signature_review', label: 'Signature Review', statuses: ['signature_review'], percent: 80 },
  { key: 'completed', label: 'Completed', statuses: ['completed'], percent: 100 },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'], percent: 0 },
];

const DSC_OPTIONS = [
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'applied', label: 'Applied' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
];

const DIN_STATUS_OPTIONS = [
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'De-Activated' },
];

const ENTITY_TYPES = [
  // India entity types
  { value: 'private_limited', label: 'Private Limited' },
  { value: 'opc', label: 'OPC' },
  { value: 'llp', label: 'LLP' },
  { value: 'llc', label: 'LLC' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'section_8', label: 'Section 8' },
  { value: 'non_profit', label: 'Non Profit' },
  { value: 'society_registered', label: 'Society Registered' },
];

// Compute service status from expiry date — NEVER use stored status
const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [linkedTicket, setLinkedTicket] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Section expand states
  const [expandCompany, setExpandCompany] = useState(false);
  const [expandFounders, setExpandFounders] = useState(false);
  
  // Edit states
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({});
  const [savingCompany, setSavingCompany] = useState(false);
  
  const [editingFounder, setEditingFounder] = useState(null);
  const [founderForm, setFounderForm] = useState({});
  const [savingFounder, setSavingFounder] = useState(false);
  
  // View Entity Modal
  const [viewingEntity, setViewingEntity] = useState(null);
  const [viewingEntityType, setViewingEntityType] = useState(null);
  
  // Add Director modal
  const [showAddDirector, setShowAddDirector] = useState(false);
  const [addingDirector, setAddingDirector] = useState(false);
  const [newDirectorForm, setNewDirectorForm] = useState({
    full_name: '', email: '', phone: '', date_of_birth: '', pan_number: '', aadhaar_number: '',
    din_number: '', din_status: 'not_applied', dsc_status: 'not_applied',
    dsc_expiry: '', dsc_expiry_date: '', dsc_class: '', dsc_provider: '',
    is_authorized_signatory: false, address: '',
  });
  
  // Add Shareholder modal
  const [showAddShareholder, setShowAddShareholder] = useState(false);
  const [addingShareholder, setAddingShareholder] = useState(false);
  const [newShareholderForm, setNewShareholderForm] = useState({
    shareholder_name: '', shareholder_email: '', shareholder_type: 'individual',
    number_of_shares: 0, linked_director_id: '', pan_number: '',
  });
  
  // Filing Status — local state (Stage 4 UI only, API wired in Stage 5)
  const FILING_FREQUENCIES = ['monthly', 'quarterly', 'yearly', 'custom'];
  const isFilingProduct = FILING_FREQUENCIES.includes(order?.product?.frequency_settings?.frequency);
  const isGSTProduct = order?.product?.department_type?.toUpperCase() === 'GST' || order?.order?.service_type === 'gst';
  const isDurationBased = order?.product?.frequency_settings?.frequency === 'duration_based';

  // Payment gate: KYC/info/docs only shown after payment is verified
  const PAYMENT_VERIFIED_STATUSES = ['payment_received', 'documents_pending', 'pending_documents', 'under_review', 'info_submitted', 'signature_pending', 'signature_review', 'completed'];
  const isPaymentVerified = PAYMENT_VERIFIED_STATUSES.includes(order?.order?.status);

  // Service Duration state (for duration_based products)
  const [durationMonths, setDurationMonths] = useState(order?.order?.duration_months || null);
  const [savingDuration, setSavingDuration] = useState(false);

  // GST Sheet preparation state (Admin)
  const [gstPreparing, setGstPreparing] = useState(false);
  const [gstJobId, setGstJobId] = useState(null);
  const [gstSheetStatus, setGstSheetStatus] = useState(null);

  // Poll for GST sheet status
  useEffect(() => {
    if (!gstJobId || !order?.order?.id) return;
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/admin/orders/${order.order.id}/gst/sheet-status/${gstJobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.status === 'completed') {
          clearInterval(interval);
          setGstPreparing(false);
          setGstJobId(null);
          setGstSheetStatus('completed');
          toast.success('GST sheet ready! View in Filing Boards for full details.');
          fetchOrder();
        } else if (data.status === 'failed') {
          clearInterval(interval);
          setGstPreparing(false);
          setGstJobId(null);
          setGstSheetStatus('failed');
          toast.error(`Sheet preparation failed: ${data.error || 'Unknown error'}`);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [gstJobId, order?.order?.id]);

  const handlePrepareGstSheet = async () => {
    setGstPreparing(true);
    setGstSheetStatus(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/orders/${order.order.id}/gst/prepare-sheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.job_id) {
        setGstJobId(data.job_id);
        toast.info('Preparing GST sheet... this may take 30-60 seconds');
      } else if (data.status === 'already_processing') {
        toast.info('Sheet is already being prepared');
      }
    } catch (err) {
      setGstPreparing(false);
      toast.error('Failed to start sheet preparation');
    }
  };

  // Sync duration state when order data loads
  useEffect(() => {
    if (order?.order?.duration_months) {
      setDurationMonths(order.order.duration_months);
    }
  }, [order?.order?.duration_months]);

  // Fetch KYC gap data for both admin and user
  const [kycGapData, setKycGapData] = useState(null);
  useEffect(() => {
    if (!order?.order?.id) return;
    const fetchKycGap = async () => {
      try {
        const data = await ordersApi.getKycGap(order.order.id);
        setKycGapData(data);
        // Auto-expand Founders section if there are rejected documents
        if (data?.has_rejected || data?.summary?.documents_rejected > 0) {
          setExpandFounders(true);
        }
      } catch { setKycGapData(null); }
    };
    fetchKycGap();
  }, [order?.order?.id]);

  const FILING_STATUS_OPTIONS = [
    { value: 'not_started',                   label: 'Not Started' },
    { value: 'draft_in_progress',             label: 'Draft in Progress' },
    { value: 'draft_sent_to_client',          label: 'Draft Sent to Client' },
    { value: 'awaiting_client_approval',      label: 'Awaiting Client Approval' },
    { value: 'client_approved',               label: 'Client Approved' },
    { value: 'client_rejected',               label: 'Client Rejected' },
    { value: 'filing_in_progress',            label: 'Filing in Progress' },
    { value: 'filed_awaiting_acknowledgement',label: 'Filed — Awaiting Acknowledgement' },
    { value: 'filing_completed',              label: 'Filing Completed' },
    { value: 'clarification_required',        label: 'Clarification Required' },
    { value: 'clarification_submitted',       label: 'Clarification Submitted' },
  ];

  const STATUS_COLOR = {
    not_started:                    'bg-slate-500/20 text-slate-400 border-emerald-800/30',
    draft_in_progress:              'bg-blue-500/20 text-blue-400 border-blue-500/30',
    draft_sent_to_client:           'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    awaiting_client_approval:       'bg-amber-500/20 text-amber-400 border-amber-500/30',
    client_approved:                'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    client_rejected:                'bg-red-500/20 text-red-400 border-red-500/30',
    filing_in_progress:             'bg-purple-500/20 text-purple-400 border-purple-500/30',
    filed_awaiting_acknowledgement: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    filing_completed:               'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    clarification_required:         'bg-orange-500/20 text-orange-400 border-orange-500/30',
    clarification_submitted:        'bg-teal-500/20 text-teal-400 border-teal-500/30',
  };

  const existingFS = order?.order?.filing_status || {};
  const [fsForm, setFsForm] = useState({
    status:           existingFS.status           || 'not_started',
    remark:           existingFS.remark           || '',
    reference_number: existingFS.reference_number || '',
  });
  const [savingFS, setSavingFS]       = useState(false);

  // Sync fsForm with order data when it changes (e.g., after re-fetch)
  useEffect(() => {
    const fs = order?.order?.filing_status || {};
    setFsForm({
      status:           fs.status           || 'not_started',
      remark:           fs.remark           || '',
      reference_number: fs.reference_number || '',
    });
  }, [order?.order?.filing_status]);

  // Client action local state
  const [clientAction, setClientAction]   = useState(null); // 'approved' | 'rejected'
  const [clientRemark, setClientRemark]   = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [legalDeclarations, setLegalDeclarations] = useState({
    reviewedDocuments: false,
    understandAmendment: false,
    authorizeSubmission: false,
  });
  const [uploadingReqId, setUploadingReqId] = useState(null);
  const [uploadingSignedDocId, setUploadingSignedDocId] = useState(null);
  const [docActionForm, setDocActionForm] = useState({}); // { docId: { showForm: 'approve'|'reject', remark: '' } }
  const [rejectionPopup, setRejectionPopup] = useState({ show: false, doc: null }); // client-visible rejection modal
  const [textInputs, setTextInputs] = useState({});
  const [submittingText, setSubmittingText] = useState(null);
  const [submittingForReview, setSubmittingForReview] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const fileInputRef = useRef({});
  const signedFileInputRef = useRef({});

  // GST Sheet Review — client confirm/correction
  const [gstConfirming, setGstConfirming] = useState(false);
  const [gstCorrectionText, setGstCorrectionText] = useState('');
  const [showGstCorrection, setShowGstCorrection] = useState(false);
  const [gstCorrectionSubmitting, setGstCorrectionSubmitting] = useState(false);

  // Admin-only: Internal Notes & Activity Log
  // viewAsClient=true (URL query) flips this admin into a read-only client preview.
  const viewAsClient = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('viewAsClient') === 'true';
  const isAdmin = !viewAsClient && (user?.role === 'admin' || user?.role === 'super_admin');
  const isAnyUser = !!user; // any authenticated user can see activity log
  const [adminTab, setAdminTab] = useState(null); // 'notes' | 'activity' | null
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [noteContent, setNoteContent] = useState('');
  const [noteFile, setNoteFile] = useState(null);
  const [noteFileName, setNoteFileName] = useState('');
  const [postingNote, setPostingNote] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const noteFileRef = useRef(null);

  // DSC Consent state (client view)
  const [dscConsent, setDscConsent] = useState(null);
  const [dscResponding, setDscResponding] = useState(false);
  const [dscCheckbox, setDscCheckbox] = useState(false);

  useEffect(() => {
    if (!orderId || isAdmin) return;
    dscConsentApi.getForOrder(orderId).then(res => setDscConsent(res?.consent || null)).catch(() => {});
  }, [orderId, isAdmin, lastRefresh]);

  const handleDscResponse = async (action) => {
    if (!dscConsent?.id) return;
    setDscResponding(true);
    try {
      await dscConsentApi.respond(dscConsent.id, action);
      toast.success(action === 'approve' ? 'DSC consent approved successfully' : 'DSC consent rejected');
      setDscCheckbox(false);
      fetchOrder();
      const res = await dscConsentApi.getForOrder(orderId);
      setDscConsent(res?.consent || null);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to respond'));
    } finally {
      setDscResponding(false);
    }
  };

  // toIST is now imported from utils/dateUtils

  useEffect(() => {
    if (!order?.order?.id) return;
    if (adminTab === 'notes' && isAdmin) {
      setLoadingNotes(true);
      adminApi.getOrderNotes(order.order.id)
        .then(setNotes).catch(() => setNotes([]))
        .finally(() => setLoadingNotes(false));
    }
    if (adminTab === 'activity') {
      setLoadingActivity(true);
      adminApi.getOrderActivity(order.order.id)
        .then(setActivity).catch(() => setActivity([]))
        .finally(() => setLoadingActivity(false));
    }
  }, [adminTab, order?.order?.id, isAdmin]);

  // Polling: refresh notes every 10 seconds when Notes tab is open (admin only)
  useEffect(() => {
    if (!isAdmin || adminTab !== 'notes' || !order?.order?.id) return;
    const interval = setInterval(() => {
      adminApi.getOrderNotes(order.order.id)
        .then(setNotes).catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [adminTab, order?.order?.id, isAdmin]);

  // Polling: refresh activity every 10 seconds when Activity tab is open
  useEffect(() => {
    if (adminTab !== 'activity' || !order?.order?.id) return;
    const interval = setInterval(() => {
      adminApi.getOrderActivity(order.order.id)
        .then(setActivity).catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [adminTab, order?.order?.id]);

  const handlePostNote = async () => {
    if (!noteContent.trim()) return;
    setPostingNote(true);
    try {
      await adminApi.createOrderNote(order.order.id, noteContent.trim(), noteFile);
      setNoteContent('');
      setNoteFile(null);
      setNoteFileName('');
      toast.success('Note posted');
      const fresh = await adminApi.getOrderNotes(order.order.id);
      setNotes(fresh);
    } catch {
      toast.error('Failed to post note');
    } finally {
      setPostingNote(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [orderId, user?.role]);

  // Auto-trigger Razorpay when the client arrives via /orders/:id?action=pay
  // (from the "Pay now" card on the client Dashboard). One-shot: we strip the
  // param so refresh doesn't re-open the modal.
  const [autoPayTried, setAutoPayTried] = useState(false);
  useEffect(() => {
    if (autoPayTried) return;
    if (!order?.order?.id) return;
    if (isAdmin) return;
    const action = new URLSearchParams(window.location.search).get('action');
    if (action !== 'pay') return;

    const status = String(order.order.status || '').toLowerCase();
    const paid = String(order.order.payment_status || '').toLowerCase() === 'paid';
    const total = Number(order.order.total_amount || 0);
    const canPay = !paid && total > 0 && (status === 'draft' || status === 'payment_pending');

    setAutoPayTried(true);
    // Strip ?action=pay from the URL so a page refresh doesn't re-fire.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('action');
      window.history.replaceState({}, '', url.toString());
    } catch { /* noop */ }

    if (canPay) {
      // Defer one tick so Razorpay script and DOM are ready.
      setTimeout(() => { handleOnlinePayment(); }, 50);
    }
  }, [order?.order?.id, isAdmin, autoPayTried]);

  // Fetch recurring pause status for admin on recurring orders
  useEffect(() => {
    const fetchPauseStatus = async () => {
      if (!isAdmin || !order?.order?.is_recurring || !order?.order?.company_id || !order?.order?.product_id) return;
      try {
        const status = await recurringPauseApi.getStatus(order.order.company_id, order.order.product_id);
        setRecurringPauseStatus(status);
      } catch { setRecurringPauseStatus(null); }
    };
    fetchPauseStatus();
  }, [order?.order?.company_id, order?.order?.product_id, order?.order?.is_recurring, isAdmin]);

  // Auto-refresh every 30 seconds to get updates from admin
  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrder(true); // silent refresh
      setLastRefresh(new Date());
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [orderId]);

  // Refresh when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchOrder(true);
        setLastRefresh(new Date());
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [orderId]);

  const fetchOrder = async (silent = false) => {
    try {
      const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
      const data = isAdminUser
        ? await adminApi.getOrderDetails(orderId)
        : await myOrdersApi.getById(orderId);
      console.log('[fetchOrder] role:', user?.role, '| isAdminUser:', isAdminUser, '| freq:', data?.product?.frequency_settings?.frequency);
      setOrder(data);
      // Fetch linked support ticket (auto-created with future orders)
      try {
        const res = await api.get(`/tickets/by-order/${orderId}`);
        setLinkedTicket(res.data || null);
      } catch { setLinkedTicket(null); }
      // Auto-show rejection popup for clients when a signature doc is rejected
      // and the client hasn't dismissed this specific rejection attempt yet.
      if (!isAdminUser && Array.isArray(data?.signature_documents)) {
        const rejectedDoc = data.signature_documents.find(d => d.status === 'rejected');
        if (rejectedDoc) {
          const dismissKey = `sig_reject_seen:${rejectedDoc.id}:${(rejectedDoc.rejection_history || []).length}`;
          if (!localStorage.getItem(dismissKey)) {
            setRejectionPopup({ show: true, doc: rejectedDoc });
          }
        }
      }
      if (data.company) {
        setCompanyForm({
          company_name: data.company.company_name || '',
          entity_type: data.company.entity_type || '',
          state: data.company.state || '',
          description: data.company.description || '',
          cin_llpin: data.company.cin_llpin || '',
          registered_address: data.company.registered_address || '',
          company_phone: data.company.company_phone || '',
          company_email: data.company.company_email || '',
          authorized_capital: data.company.capital_structure?.authorized_capital || '',
          paid_up_capital: data.company.capital_structure?.paid_up_capital || '',
          website: data.company.website || '',
          date_of_incorporation: data.company.date_of_incorporation || '',
        });
      }
      if (!silent) {
        // Only show loading states for manual refreshes
      }
    } catch (error) {
      if (!silent) {
        const status = error?.response?.status;
        // Only redirect on auth errors (401), not on other errors like 403/500
        if (status === 401) {
          navigate('/login');
        } else {
          toast.error('Failed to load order details');
          setLoading(false);
        }
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleManualRefresh = () => {
    toast.info('Refreshing...');
    fetchOrder(true);
    setLastRefresh(new Date());
    setTimeout(() => toast.success('Updated!'), 500);
  };

  const getCurrentStep = () => {
    if (!order) return 0;
    const status = order.order.status;
    for (let i = 0; i < PROGRESS_STEPS.length; i++) {
      if (PROGRESS_STEPS[i].statuses.includes(status)) {
        return i;
      }
    }
    return 0;
  };

  const getProgressPercent = () => {
    const step = getCurrentStep();
    return PROGRESS_STEPS[step]?.percent || 25;
  };

  // Bank Payment Handler - Show bank details and "I have Paid" button
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [submittingPaymentConfirmation, setSubmittingPaymentConfirmation] = useState(false);
  const [processingOnlinePayment, setProcessingOnlinePayment] = useState(false);
  const [processingStripePayment, setProcessingStripePayment] = useState(false);

  // Per-company recurring pause state
  const [recurringPauseStatus, setRecurringPauseStatus] = useState(null);
  const [pauseLoading, setPauseLoading] = useState(false);

  const handleRecurringPause = async () => {
    const reason = window.prompt('Enter reason for pausing future orders for this company (required):');
    if (!reason || !reason.trim()) return;
    setPauseLoading(true);
    try {
      await recurringPauseApi.pause(order.order.company_id, order.order.product_id, reason.trim());
      toast.success('Future recurring orders paused for this company');
      const status = await recurringPauseApi.getStatus(order.order.company_id, order.order.product_id);
      setRecurringPauseStatus(status);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to pause'));
    } finally { setPauseLoading(false); }
  };

  const handleRecurringResume = async () => {
    if (!window.confirm('Resume generating future recurring orders for this company?')) return;
    setPauseLoading(true);
    try {
      await recurringPauseApi.resume(order.order.company_id, order.order.product_id);
      toast.success('Recurring orders resumed for this company');
      const status = await recurringPauseApi.getStatus(order.order.company_id, order.order.product_id);
      setRecurringPauseStatus(status);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to resume'));
    } finally { setPauseLoading(false); }
  };

  const handlePayment = async () => {
    setShowBankDetails(true);
  };

  const handlePaymentConfirmation = async () => {
    setSubmittingPaymentConfirmation(true);
    try {
      // Submit payment confirmation to backend
      await paymentsApi.submitPaymentConfirmation(orderId);
      toast.success('Payment confirmation submitted! We will verify and confirm once payment is received.');
      setShowBankDetails(false);
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to submit payment confirmation'));
    } finally {
      setSubmittingPaymentConfirmation(false);
    }
  };

  // Stripe Payment Handler (US Orders)
  const handleStripePayment = async () => {
    setProcessingStripePayment(true);
    try {
      const checkoutData = await paymentsApi.createStripeCheckout(orderId);
      // Redirect to Stripe checkout
      window.location.href = checkoutData.checkout_url;
    } catch (error) {
      toast.error(getApiError(error, 'Failed to initiate payment'));
      setProcessingStripePayment(false);
    }
  };

  // Razorpay Online Payment Handler
  const handleOnlinePayment = async () => {
    setProcessingOnlinePayment(true);
    try {
      // Create Razorpay order
      const paymentData = await paymentsApi.createPaymentOrder(orderId);
      
      // Open Razorpay checkout
      const options = {
        key: paymentData.razorpay_key_id,
        amount: paymentData.amount_paise,
        currency: paymentData.currency,
        name: 'Snigdha & Associates',
        description: paymentData.notes?.product_name || 'Service Payment',
        order_id: paymentData.razorpay_order_id,
        prefill: {
          name: paymentData.prefill?.name || '',
          email: paymentData.prefill?.email || '',
          contact: paymentData.prefill?.contact || ''
        },
        notes: paymentData.notes || {},
        theme: {
          color: '#10b981'
        },
        handler: async function(response) {
          // Verify payment on backend
          try {
            await paymentsApi.verifyPayment(
              orderId,
              response.razorpay_payment_id,
              response.razorpay_order_id,
              response.razorpay_signature
            );
            toast.success('Payment successful! You can now submit documents.');
            setShowBankDetails(false);
            fetchOrder();
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: function() {
            setProcessingOnlinePayment(false);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function(response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setProcessingOnlinePayment(false);
      });
      razorpay.open();
      
    } catch (error) {
      toast.error(getApiError(error, 'Failed to initiate payment'));
      setProcessingOnlinePayment(false);
    }
  };

  const handleRequestInvoice = async () => {
    setProcessing(true);
    try {
      await myOrdersApi.requestInvoice(orderId);
      toast.success('Invoice requested! You will receive it via email within 24 hours.');
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to request invoice'));
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveCompany = async () => {
    setSavingCompany(true);
    try {
      // Build update payload — India-only platform
      const updatePayload = {
        company_name: companyForm.company_name,
        entity_type: companyForm.entity_type,
        state: companyForm.state,
        description: companyForm.description,
        company_phone: companyForm.company_phone,
        company_email: companyForm.company_email,
        cin_llpin: companyForm.cin_llpin,
        registered_address: companyForm.registered_address,
        capital_structure: {
          authorized_capital: companyForm.authorized_capital ? Number(companyForm.authorized_capital) : null,
          paid_up_capital: companyForm.paid_up_capital ? Number(companyForm.paid_up_capital) : null,
        },
      };
      
      await companiesApi.update(order.company.id, updatePayload);
      toast.success('Company information updated');
      setEditingCompany(false);
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to update company'));
    } finally {
      setSavingCompany(false);
    }
  };

  const handleEditFounder = (founder, type) => {
    setEditingFounder({ id: founder.id, type });
    setFounderForm({
      full_name: founder.full_name || founder.shareholder_name || '',
      email: founder.email || founder.shareholder_email || '',
      phone: founder.phone || '',
      date_of_birth: founder.date_of_birth || '',
      pan_number: founder.pan_number || '',
      aadhaar_number: founder.aadhaar_number || '',
      dsc_status: founder.dsc_status || 'not_applied',
      dsc_expiry: founder.dsc_expiry || '',
      is_authorized_signatory: founder.is_authorized_signatory || false,
      din_number: founder.din_number || '',
      din_status: founder.din_status || 'not_applied',
    });
  };

  const handleSaveFounder = async () => {
    setSavingFounder(true);
    try {
      if (editingFounder.type === 'director') {
        await directorsApi.update(editingFounder.id, founderForm);
      } else {
        await companiesApi.updateShareholder(editingFounder.id, {
          shareholder_name: founderForm.full_name,
          shareholder_email: founderForm.email,
          pan_number: founderForm.pan_number,
          aadhaar_number: founderForm.aadhaar_number,
          dsc_status: founderForm.dsc_status,
          dsc_expiry: founderForm.dsc_expiry || null,
          date_of_birth: founderForm.date_of_birth || null,
          is_authorized_signatory: founderForm.is_authorized_signatory,
          din_number: founderForm.din_number,
          din_status: founderForm.din_status,
        });
      }
      toast.success('Information updated');
      setEditingFounder(null);
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to update'));
    } finally {
      setSavingFounder(false);
    }
  };

  const handleAddDirector = async () => {
    if (!newDirectorForm.full_name || !newDirectorForm.email) {
      toast.error('Name and email are required');
      return;
    }
    setAddingDirector(true);
    try {
      // Create director with correct country tag
      const director = await directorsApi.create({
        ...newDirectorForm,
        country: 'IN'
      });
      await companiesApi.addDirector(order.company.id, director.id);
      toast.success('Director added successfully');
      setShowAddDirector(false);
      setNewDirectorForm({
        full_name: '', email: '', phone: '', date_of_birth: '', pan_number: '', aadhaar_number: '',
        din_number: '', din_status: 'not_applied', dsc_status: 'not_applied',
        dsc_expiry: '', is_authorized_signatory: false, address: '',
      });
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to add director'));
    } finally {
      setAddingDirector(false);
    }
  };

  const handleAddShareholder = async () => {
    if (!newShareholderForm.shareholder_name || newShareholderForm.number_of_shares <= 0) {
      toast.error('Name and number of shares are required');
      return;
    }
    setAddingShareholder(true);
    try {
      await companiesApi.addShareholder(order.company.id, {
        ...newShareholderForm,
        linked_director_id: newShareholderForm.linked_director_id || null,
      });
      toast.success('Shareholder added successfully');
      setShowAddShareholder(false);
      setNewShareholderForm({
        shareholder_name: '', shareholder_email: '', shareholder_type: 'individual',
        number_of_shares: 0, linked_director_id: '', pan_number: '',
      });
      fetchOrder();
    } catch (error) {
      // Handle different error formats
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to add shareholder';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || 'Validation error').join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setAddingShareholder(false);
    }
  };

  const handleFileUpload = async (requirementId, file) => {
    if (!file) return;
    setUploadingReqId(requirementId);
    try {
      await myOrdersApi.uploadDocument(orderId, requirementId, file);
      toast.success('Document uploaded successfully');
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Upload failed'));
    } finally {
      setUploadingReqId(null);
    }
  };

  const handleTextSubmit = async (requirementId) => {
    const value = textInputs[requirementId];
    if (!value || !value.trim()) {
      toast.error('Please enter a value');
      return;
    }
    setSubmittingText(requirementId);
    try {
      await myOrdersApi.submitText(orderId, requirementId, value.trim());
      toast.success('Information submitted');
      setTextInputs(prev => ({ ...prev, [requirementId]: '' }));
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to submit'));
    } finally {
      setSubmittingText(null);
    }
  };

  const handleSignedDocUpload = async (docId, file, action = null, remark = '') => {
    // For filing products: action can be 'approve' or 'reject' (no file upload)
    // For non-filing products: file upload required
    if (!file && !action) return;
    
    setUploadingSignedDocId(docId);
    try {
      if (action) {
        // Filing product approve/reject - call the client action endpoint
        await myOrdersApi.approveRejectSignatureDoc(orderId, docId, action, remark);
        toast.success(action === 'approve' 
          ? 'Document approved successfully' 
          : 'Change request submitted. Admin will review and update the document.');
        // Clear the form
        setDocActionForm(prev => ({ ...prev, [docId]: { showForm: null, remark: '' } }));
      } else {
        // Non-filing product - upload signed copy
        await myOrdersApi.uploadSignedDocument(orderId, docId, file);
        toast.success('Signed Documents Submitted for Review. We have received your request. We will confirm as soon as possible via email.');
      }
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, action ? 'Action failed' : 'Upload failed'));
    } finally {
      setUploadingSignedDocId(null);
    }
  };

  const handleSubmitForReview = async () => {
    setSubmittingForReview(true);
    try {
      await myOrdersApi.submitDocuments(orderId);
      toast.success('All documents submitted for review');
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to submit'));
    } finally {
      setSubmittingForReview(false);
    }
  };

  // ── GST Sheet Client Actions ──
  const handleGstConfirm = async () => {
    setGstConfirming(true);
    try {
      const amount = order?.order?.amount || 0;
      const isExempt = order?.order?.exempt_from_payment || order?.order?.payment_status === 'prepaid';

      if (amount > 0 && !isExempt) {
        // Post-confirmation payment via Razorpay (per-filing clients only)
        const paymentData = await paymentsApi.createGstPayment(orderId);

        const options = {
          key: paymentData.razorpay_key_id,
          amount: paymentData.amount_paise,
          currency: paymentData.currency,
          name: 'Snigdha & Associates',
          description: `GST Filing — ${paymentData.notes?.period || ''}`,
          order_id: paymentData.razorpay_order_id,
          prefill: paymentData.prefill || {},
          notes: paymentData.notes || {},
          theme: { color: '#10b981' },
          handler: async function(response) {
            try {
              await paymentsApi.verifyGstPayment(
                orderId,
                response.razorpay_payment_id,
                response.razorpay_order_id,
                response.razorpay_signature
              );
              toast.success('Payment successful! GST data confirmed. Filing will proceed.');
              fetchOrder();
            } catch (err) {
              toast.error('Payment verification failed. Contact support.');
            } finally {
              setGstConfirming(false);
            }
          },
          modal: {
            ondismiss: function() {
              setGstConfirming(false);
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', function(resp) {
          toast.error(`Payment failed: ${resp.error.description}`);
          setGstConfirming(false);
        });
        razorpay.open();
      } else {
        // No amount — free confirm (fallback)
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/orders/${orderId}/gst/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed');
        toast.success('GST data confirmed. Filing will proceed.');
        fetchOrder();
        setGstConfirming(false);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to confirm');
      setGstConfirming(false);
    }
  };

  const handleGstCorrection = async () => {
    if (!gstCorrectionText.trim()) { toast.error('Please describe the issue'); return; }
    setGstCorrectionSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/orders/${orderId}/gst/raise-correction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ issue: gstCorrectionText.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Correction submitted. Our team will review.');
      setGstCorrectionText('');
      setShowGstCorrection(false);
      fetchOrder();
    } catch (error) {
      toast.error(error.message || 'Failed to submit correction');
    } finally {
      setGstCorrectionSubmitting(false);
    }
  };

  // ── Filing Status Handlers ──
  const handleSaveFilingStatus = async () => {
    setSavingFS(true);
    try {
      await adminApi.updateFilingStatus(order.order.id, {
        status: fsForm.status,
        remark: fsForm.remark || null,
        reference_number: fsForm.reference_number || null,
      });
      toast.success('Filing status updated');
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to update filing status'));
    } finally {
      setSavingFS(false);
    }
  };

  const handleClientFilingAction = async (action) => {
    if (!clientRemark.trim()) {
      toast.error('Please add a remark before submitting');
      return;
    }
    setSubmittingAction(true);
    try {
      await adminApi.clientFilingAction(order.order.id, action, clientRemark.trim());
      toast.success(`Filing ${action === 'approved' ? 'approved' : 'rejected'} successfully`);
      setShowApproveForm(false);
      setShowRejectForm(false);
      setClientRemark('');
      setClientAction(null);
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, `Failed to ${action === 'approved' ? 'approve' : 'reject'} filing`));
    } finally {
      setSubmittingAction(false);
    }
  };

  // ── Service Duration Handler (Duration-Based Products) ──
  const handleSaveDuration = async () => {
    if (!durationMonths) {
      toast.error('Please select a service duration');
      return;
    }
    setSavingDuration(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/orders/${order.order.id}/duration?duration_months=${durationMonths}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save duration');
      }
      toast.success(`Duration set to ${durationMonths} months`);
      fetchOrder();
    } catch (error) {
      toast.error(error.message || 'Failed to save duration');
    } finally {
      setSavingDuration(false);
    }
  };

  const getSubmission = (reqId) => order?.submissions?.find(s => s.requirement_id === reqId);
  const getSubmissions = (reqId) => order?.submissions?.filter(s => s.requirement_id === reqId) || [];
  
  const handleDeleteSubmission = async (submissionId) => {
    try {
      await myOrdersApi.deleteSubmission(orderId, submissionId);
      toast.success('Document removed');
      fetchOrder();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete'));
    }
  };

  // Check if all documents are ready (requirements + signatures)
  // Only REQUIRED items block submission. Optional items can be left blank.
  const allDocumentsReady = () => {
    const reqs = order?.requirements || [];
    const signatures = order?.signature_documents || [];

    // Treat is_required === false (or 0) as optional. Default to required when flag is missing.
    const requiredReqs = reqs.filter(r => r.is_required !== false);
    const allRequiredSubmitted = requiredReqs.every(r => getSubmissions(r.id).length > 0);
    const allSignaturesSigned = signatures.every(d => d.signed_url);

    return allRequiredSubmitted && allSignaturesSigned;
  };

  const canEditDocuments = () => {
    const blockedStatuses = ['completed', 'cancelled'];
    return !blockedStatuses.includes(order?.order?.status);
  };

  const isInDocumentsPhase = () => {
    return ['payment_received', 'documents_pending', 'pending_documents', 'under_review', 'info_submitted', 'signature_pending', 'signature_review'].includes(order?.order?.status);
  };

  const hasSignatureDocuments = () => {
    return order?.signature_documents?.length > 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1310] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!order) return null;

  const currentStep = getCurrentStep();
  const progressPercent = getProgressPercent();
  const totalDocsCount = (order.requirements?.length || 0) + (order.signature_documents?.length || 0);
  const reqsWithSubmissions = new Set(order.submissions?.map(s => s.requirement_id) || []).size;
  const completedDocsCount = reqsWithSubmissions + (order.signature_documents?.filter(d => d.signed_url).length || 0);

  return (
    <div
      className="min-h-screen bg-slate-950 text-white"
      style={{
        fontFamily: "'IBM Plex Sans', sans-serif",
        // When iframed in 'View as Client' mode, lock all interactions but keep scroll/visual.
        ...(viewAsClient ? { pointerEvents: 'none', userSelect: 'text' } : {}),
      }}
      data-view-as-client={viewAsClient ? 'true' : 'false'}
    >
      {/* Header */}
      <header className="bg-slate-950/80 backdrop-blur-xl border-b border-emerald-900/40 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')} 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors duration-200 group"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 group-hover:-translate-x-0.5 transition-transform" />
            <span className="text-sm sm:text-base hidden sm:inline">Back to Dashboard</span>
            <span className="text-sm sm:hidden">Back</span>
          </button>
          <div className="flex items-center gap-3">
            <span className="text-[10px] sm:text-xs text-slate-500 hidden md:inline">
              {lastRefresh.toLocaleTimeString()}
            </span>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={handleManualRefresh}
              className="text-slate-400 border-emerald-900/40 hover:bg-[#0f1611] hover:text-white hover:border-emerald-800/40 text-xs sm:text-sm px-3 transition-colors duration-200 active:scale-[0.97]"
            >
              <Clock className="w-3.5 h-3.5 sm:mr-1.5" /> 
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Company Header */}
        <div className="mb-6 sm:mb-10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div>
              <h1 className="text-xl sm:text-3xl font-extrabold tracking-tight text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{order.company?.company_name}</h1>
              <p className="text-sm sm:text-base text-slate-400" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                {ENTITY_TYPES.find(e => e.value === order.company?.entity_type)?.label || order.company?.entity_type} &bull; {order.company?.state}
              </p>
            </div>
          </div>
        </div>

        {/* Document Rejection Banner — Show only on client view when any submission was rejected.
            Critical UX: it must be loud (red) about the document, but always reassure that the
            order itself is still active so the client doesn't think the order is dead. */}
        {!isAdmin && (() => {
          const terminal = ['completed', 'cancelled', 'canceled'].includes(String(order.order?.status || '').toLowerCase());
          const rejectedSubs = (order.submissions || []).filter(s => s?.status === 'rejected');
          if (terminal || rejectedSubs.length === 0) return null;
          const reqsById = new Map((order.requirements || []).map(r => [r.id, r]));
          return (
            <div
              className="mb-6 bg-red-500/10 border border-red-500/40 rounded-2xl p-5"
              data-testid="documents-need-attention-banner"
            >
              <div className="flex items-start gap-3 mb-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-red-300" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    One or more documents need attention
                  </h3>
                  <p className="text-sm text-slate-300 mt-1">
                    Your order is still active. Please re-upload the corrected document(s) below to continue.
                  </p>
                </div>
              </div>
              <div className="space-y-2 ml-8">
                {rejectedSubs.map(sub => {
                  const req = reqsById.get(sub.requirement_id);
                  const docLabel = req?.label || sub.value || 'Document';
                  return (
                    <div key={sub.id} className="bg-[#0b1310]/60 border border-red-500/20 rounded-lg p-3" data-testid={`rejected-doc-${sub.requirement_id}`}>
                      <p className="text-sm font-medium text-white">{docLabel}</p>
                      {sub.admin_comment && (
                        <p className="text-xs text-slate-400 mt-1">
                          <span className="text-red-300 font-medium">Reason:</span> {sub.admin_comment}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">

            {/* ═══ Active Services (top for all users) ═══════════════ */}
            <div className="bg-[#0f1611] rounded-2xl border border-emerald-900/40 overflow-hidden" data-testid="active-services-card">
              <div className="px-5 py-4 border-b border-emerald-900/40 flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-emerald-400" />
                </div>
                <h2 className="text-base font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Active Service</h2>
                <button
                  onClick={async () => {
                    try {
                      // Get-or-create ticket for THIS specific order
                      let t = linkedTicket;
                      if (!t) {
                        const res = await api.post(`/tickets/by-order/${orderId}/get-or-create`);
                        t = res.data;
                        setLinkedTicket(t);
                      }
                      const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
                      if (isAdminUser) navigate(`/admin?tab=tickets&ticket=${t.id}`);
                      else navigate(`/dashboard?section=tickets&ticket=${t.id}`);
                    } catch (e) {
                      const msg = e?.response?.data?.detail || 'Could not open support ticket';
                      toast.error(msg);
                    }
                  }}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25"
                  data-testid="order-open-ticket-btn"
                  title={linkedTicket ? `Ticket #${linkedTicket.ticket_number} · ${linkedTicket.status}` : 'Start a communication thread for this order'}
                >
                  <LifeBuoy className="w-3.5 h-3.5" />
                  {linkedTicket ? `Open Communication ${linkedTicket.conversation_id || '#' + linkedTicket.ticket_number}` : 'Open Communication'}
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between mb-3 gap-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2 flex-wrap">
                      {order.order?.display_name || order.product?.name || 'Service'}
                      {order.order?.is_renewal && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">Renewal</span>
                      )}
                    </h3>
                    {order.product?.description && <p className="text-slate-400 text-sm mt-1">{order.product.description}</p>}
                    {(order.product?.category || order.product?.sub_category || order.product?.time_to_complete_days) && (
                      <div className="flex flex-wrap gap-2 mt-2.5">
                        {order.product?.category && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] bg-[#152219] text-slate-300 border border-emerald-900/40">
                            <Tag className="w-3 h-3 text-emerald-400/70" /> {order.product.category}
                          </span>
                        )}
                        {order.product?.sub_category && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] bg-[#152219] text-slate-300 border border-emerald-900/40">
                            <FileText className="w-3 h-3 text-emerald-400/70" /> {order.product.sub_category}
                          </span>
                        )}
                        {order.product?.time_to_complete_days && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] bg-[#152219] text-slate-300 border border-emerald-900/40">
                            <Clock className="w-3 h-3 text-emerald-400/70" /> {order.product.time_to_complete_days} days
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl font-semibold text-emerald-400">₹{(order.order?.final_amount || order.order?.total_amount || 0).toLocaleString('en-IN')}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{order.order?.status?.replace(/_/g, ' ')}</div>
                  </div>
                </div>
                {(order.product?.inclusions?.length > 0 || order.product?.exclusions?.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-emerald-900/30 grid md:grid-cols-2 gap-2">
                    {order.product?.inclusions?.map((inc, idx) => (
                      <div key={`inc-${idx}`} className="flex items-start gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-slate-300">{inc.title}</span>
                      </div>
                    ))}
                    {order.product?.exclusions?.map((exc, idx) => (
                      <div key={`exc-${idx}`} className="flex items-start gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-red-400/70 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-slate-500 line-through decoration-red-400/40">{exc.title}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ═══ Combined KYC Upload (client only, after payment) ══ */}
            {!isAdmin && isPaymentVerified && (order.company?.id || (order.directors?.length > 0) || (order.shareholders?.length > 0)) && (
              <div className="bg-[#0f1611] rounded-2xl border border-emerald-900/40 overflow-hidden" data-testid="client-kyc-upload-card">
                <div className="px-5 py-4 border-b border-emerald-900/40 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                      <Upload className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Upload KYC Documents</h2>
                      <p className="text-xs text-slate-500 mt-0.5">Company & director document uploads</p>
                    </div>
                  </div>
                  {kycGapData?.has_rejected && (
                    <span className="text-xs px-2 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30" data-testid="kyc-rejected-badge">
                      Action Required
                    </span>
                  )}
                </div>
                <div className="p-5 space-y-5">
                  {order.company?.id && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-emerald-400/80 font-medium mb-2">Company · {order.company.company_name}</div>
                      <KYCDocuments
                        entityType="company"
                        entityId={order.company.id}
                        entityName={order.company.company_name}
                        isAdmin={false}
                        onRefresh={fetchOrder}
                        country="IN"
                        orderId={order.order?.id}
                        orderStatus={order.order?.status}
                      />
                    </div>
                  )}
                  {order.directors?.map((director) => (
                    <div key={`dir-kyc-${director.id}`}>
                      <div className="text-[11px] uppercase tracking-wider text-emerald-400/80 font-medium mb-2">Director · {director.full_name}</div>
                      <KYCDocuments
                        entityType="director"
                        entityId={director.id}
                        entityName={director.full_name}
                        isAdmin={false}
                        onRefresh={fetchOrder}
                        country="IN"
                        orderId={order.order?.id}
                        orderStatus={order.order?.status}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Gate — for non-admin users, show KYC/info/docs only after payment */}
            {!isPaymentVerified && !isAdmin && (
              <div className="space-y-5" data-testid="payment-gate-message">
                {/* Step indicator */}
                <div className="bg-[#0b1310] rounded-2xl border border-emerald-900/40 overflow-hidden hover:border-emerald-900/40 transition-colors duration-300">
                  <div className="p-6 flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                      <Lock className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Complete Payment to Continue</h3>
                      <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">
                        Once payment is verified, you'll be able to submit your KYC documents, provide required information, and upload supporting files.
                      </p>
                    </div>
                  </div>

                  <div className="px-6 pb-6">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-[#152219]/40 rounded-xl p-3.5 text-center border border-emerald-900/25 hover:border-indigo-500/30 transition-colors duration-200">
                        <FileText className="w-4 h-4 text-indigo-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-300 font-medium">KYC Documents</p>
                      </div>
                      <div className="bg-[#152219]/40 rounded-xl p-3.5 text-center border border-emerald-900/25 hover:border-indigo-500/30 transition-colors duration-200">
                        <Upload className="w-4 h-4 text-indigo-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-300 font-medium">Information</p>
                      </div>
                      <div className="bg-[#152219]/40 rounded-xl p-3.5 text-center border border-emerald-900/25 hover:border-indigo-500/30 transition-colors duration-200">
                        <CheckCircle className="w-4 h-4 text-indigo-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-300 font-medium">Documents</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Requirements preview — compact list */}
                {(order.requirements?.length > 0 || order.product?.inclusions?.length > 0) && (
                  <div className="bg-[#0b1310] rounded-2xl border border-emerald-900/40 overflow-hidden hover:border-emerald-900/40 transition-colors duration-300">
                    <div className="p-4 px-6 border-b border-emerald-900/40 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <h3 className="text-sm font-medium text-slate-300">What You'll Need to Submit</h3>
                    </div>
                    <div className="p-4 px-6 space-y-1">
                      {order.requirements?.map((req, idx) => (
                        <div key={req.id || idx} className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-[#152219]/50 transition-colors duration-200">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/60 flex-shrink-0" />
                          <span className="text-sm text-slate-300">{req.label}</span>
                          <span className="text-[10px] text-slate-600 ml-auto uppercase tracking-wider font-medium">{req.type === 'file_upload' ? 'Upload' : 'Info'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Business Information Card — ADMIN ONLY (clients see merged KYC card above) */}
            {isAdmin && (
            <div className="bg-[#0b1310] rounded-2xl border border-emerald-900/40 overflow-hidden hover:border-emerald-900/40 transition-colors duration-300">
              <div 
                className="flex items-center justify-between p-5 cursor-pointer hover:bg-[#152219]/30 transition-colors duration-200"
                onClick={() => setExpandCompany(!expandCompany)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Business Information</h2>
                    <p className="text-xs text-slate-500">{order.company?.entity_type?.replace(/_/g, ' ')} &bull; {order.company?.state}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {!editingCompany && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setEditingCompany(true); }}
                      className="text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors duration-200"
                    >
                      Edit
                    </button>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${expandCompany ? 'rotate-180' : ''}`} />
                </div>
              </div>
              
              {expandCompany && (
                <div className="p-4 pt-0 border-t border-emerald-900/40">
                  {editingCompany ? (
                    <CompanyEditForm 
                      form={companyForm} 
                      setForm={setCompanyForm}
                      onSave={handleSaveCompany}
                      onCancel={() => setEditingCompany(false)}
                      saving={savingCompany}
                    />
                  ) : (
                    <CompanyDisplay company={order.company} />
                  )}
                  
                  {/* Company KYC Documents */}
                  {order.company?.id && (
                    <div className="mt-4 pt-4 border-t border-emerald-900/40">
                      <KYCDocuments 
                        entityType="company"
                        entityId={order.company.id}
                        entityName={order.company.company_name}
                        isAdmin={isAdmin}
                        onRefresh={fetchOrder}
                        country={'IN'}
                        orderId={order.order?.id}
                        orderStatus={order.order?.status}                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Founders & Shareholders Card — ADMIN ONLY (clients see merged KYC card above) */}
            {isAdmin && (() => {
              return (
                <div className="bg-[#0f1611] rounded-xl border border-emerald-900/40 overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#152219]/50"
                    onClick={() => setExpandFounders(!expandFounders)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/20">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-white">
                          Directors & Shareholders
                        </h2>
                        <p className="text-xs text-slate-400">
                          {order.directors?.length || 0} directors • {order.shareholders?.length || 0} shareholders
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {kycGapData?.has_rejected && (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30" data-testid="founders-rejected-badge">
                          Action Required
                        </span>
                      )}
                      {expandFounders ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
              
              {expandFounders && (
                <div className="border-t border-emerald-900/40">
                  {/* Directors Section */}
                  <div className="p-4">
                    {(() => {
                      return (
                        <>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-slate-300 uppercase tracking-wide">
                              Directors
                            </h3>
                            <button 
                              onClick={() => setShowAddDirector(true)}
                              className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center gap-1"
                            >
                              <Plus className="w-4 h-4" /> Add Director
                            </button>
                          </div>
                          
                          {order.directors?.length === 0 ? (
                            <div className="text-center py-6 bg-[#080b09]/50 rounded-lg border border-dashed border-emerald-900/40">
                              <User className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                              <p className="text-sm text-slate-500">No directors added yet</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {order.directors?.map((director) => {
                                const linkedShareholder = order.shareholders?.find(s => s.linked_director_id === director.id);
                                const isEditing = editingFounder?.id === director.id;
                                
                                return (
                                  <div key={director.id} className="bg-[#080b09]/50 rounded-lg p-4 border border-emerald-900/40 hover:border-emerald-500/50 transition-colors">
                                    {isEditing ? (
                                      <FounderEditForm form={founderForm} setForm={setFounderForm} onSave={handleSaveFounder} onCancel={() => setEditingFounder(null)} saving={savingFounder} />
                                ) : (
                                  <div className="flex items-start justify-between">
                                    <div 
                                      className="flex items-start gap-3 flex-1 cursor-pointer"
                                      onClick={() => { setViewingEntity(director); setViewingEntityType('director'); }}
                                    >
                                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-medium bg-emerald-500/20 text-emerald-400">
                                        {director.full_name?.charAt(0)?.toUpperCase() || 'D'}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <p className="font-medium text-white hover:text-emerald-400 transition-colors">{director.full_name}</p>
                                          <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">Director</span>
                                          {linkedShareholder && (
                                            <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">
                                              {linkedShareholder.number_of_shares?.toLocaleString()} shares ({linkedShareholder.percent_holding}%)
                                            </span>
                                          )}
                                          {director.is_authorized_signatory && (
                                            <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">Auth. Signatory</span>
                                          )}
                                        </div>
                                        <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
                                          <p className="text-slate-400"><Mail className="w-3 h-3 inline mr-1" /> {director.email || 'Not provided'}</p>
                                          <p className="text-slate-400"><Phone className="w-3 h-3 inline mr-1" /> {director.phone || 'Not provided'}</p>
                                          <p className="text-slate-400">DOB: {director.date_of_birth || 'Not provided'}</p>
                                          <p className="text-slate-400">PAN: {director.pan_number || 'Not provided'}</p>
                                          <p className="text-slate-400">DIN: {director.din_number || 'Not provided'}</p>
                                          <p className="text-slate-400">DSC: {director.dsc_status === 'active' ? <span className="text-emerald-400">Active</span> : director.dsc_status || 'Not applied'}</p>
                                        </div>
                                        <p className="text-xs text-emerald-500 mt-2">Click to view full details</p>
                                        
                                        {/* Director KYC Documents */}
                                        <div className="mt-3 pt-3 border-t border-emerald-900/30" onClick={(e) => e.stopPropagation()}>
                                          <KYCDocuments 
                                            entityType="director"
                                            entityId={director.id}
                                            entityName={director.full_name}
                                            isAdmin={isAdmin}
                                            onRefresh={fetchOrder}
                                            country="IN"
                                            orderId={order.order?.id}
                                            orderStatus={order.order?.status}                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); handleEditFounder(director, 'director'); }} className="text-emerald-400 hover:text-emerald-300 p-2">
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  
                  {/* Shareholders/Ownership Section */}
                  {(() => {
                    return (
                      <div className="p-4 border-t border-emerald-900/40">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-slate-300 uppercase tracking-wide">
                            Shareholders
                          </h3>
                          <button 
                            onClick={() => setShowAddShareholder(true)}
                            className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" /> Add Shareholder
                          </button>
                        </div>
                    
                        {/* Only show non-linked shareholders (linked ones show with directors) */}
                        {(() => {
                          const nonLinkedShareholders = order.shareholders?.filter(s => !order.directors?.some(d => d.id === s.linked_director_id)) || [];
                          const linkedCount = (order.shareholders?.length || 0) - nonLinkedShareholders.length;
                      
                      if (nonLinkedShareholders.length === 0 && linkedCount === 0) {
                        return (
                          <div className="text-center py-6 bg-[#080b09]/50 rounded-lg border border-dashed border-emerald-900/40">
                            <User className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">No shareholders added yet</p>
                          </div>
                        );
                      }
                      
                      return (
                        <>
                          {linkedCount > 0 && (
                            <p className="text-xs text-slate-500 mb-3">
                              {linkedCount} shareholder(s) shown above with their director profiles
                            </p>
                          )}
                          <div className="space-y-3">
                            {nonLinkedShareholders.map((shareholder) => {
                              const isEditing = editingFounder?.id === shareholder.id;
                              
                              return (
                                <div key={shareholder.id} className="bg-[#080b09]/50 rounded-lg p-4 border border-emerald-900/40 hover:border-amber-500/50 transition-colors">
                                  {isEditing ? (
                                    <FounderEditForm form={founderForm} setForm={setFounderForm} onSave={handleSaveFounder} onCancel={() => setEditingFounder(null)} saving={savingFounder} />
                                  ) : (
                                    <div className="flex items-start justify-between">
                                      <div 
                                        className="flex items-start gap-3 flex-1 cursor-pointer"
                                        onClick={() => { setViewingEntity(shareholder); setViewingEntityType('shareholder'); }}
                                      >
                                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-medium">
                                          {shareholder.shareholder_name?.charAt(0)?.toUpperCase() || 'S'}
                                        </div>
                                        <div>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-medium text-white hover:text-amber-400 transition-colors">{shareholder.shareholder_name}</p>
                                            <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">
                                              {shareholder.number_of_shares?.toLocaleString()} shares ({shareholder.percent_holding}%)
                                            </span>
                                            <span className="px-2 py-0.5 rounded text-xs bg-[#152219] text-slate-300">
                                              {shareholder.shareholder_type}
                                            </span>
                                          </div>
                                          <p className="text-sm text-slate-400 mt-1">
                                            <Mail className="w-3 h-3 inline mr-1" /> {shareholder.shareholder_email || 'Not provided'}
                                          </p>
                                          <p className="text-xs text-amber-500 mt-2">Click to view full details</p>
                                        </div>
                                      </div>
                                      <button onClick={(e) => { e.stopPropagation(); handleEditFounder(shareholder, 'shareholder'); }} className="text-emerald-400 hover:text-emerald-300 p-2">
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
              );
            })()}

            {/* REQUIREMENTS PREVIEW - Hidden, now integrated into payment gate above */}

            {/* UNIFIED DOCUMENTS SECTION - Combines requirements + signatures */}
            {isInDocumentsPhase() && (
              <div className="bg-[#0f1611] rounded-xl border border-emerald-900/40 overflow-hidden" data-testid="documents-section">
                <div className="p-4 border-b border-emerald-900/40">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-white">
                        {isGSTProduct ? `Upload your sales data for ${order?.order?.filing_period_label || 'this period'}` : isFilingProduct ? 'Filing Documents' : 'Documents'}
                      </h2>
                      <p className="text-sm text-slate-400 mt-1">
                        {isGSTProduct
                          ? 'Upload bank statement, invoices, Excel, or Tally export — any format works'
                          : isFilingProduct 
                            ? 'Review and approve filing documents'
                            : 'Upload all required documents, provide information, and sign documents'
                        }
                      </p>
                    </div>
                    {!isGSTProduct && (
                      <span className="text-sm text-slate-400">
                        {completedDocsCount} / {totalDocsCount} complete
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="p-4 space-y-4">
                  {/* GST-specific simplified upload */}
                  {isGSTProduct && (() => {
                    const gstReq = order.requirements?.[0];
                    if (!gstReq) return null;
                    const submissions = getSubmissions(gstReq.id);
                    const hasSubmissions = submissions.length > 0;
                    const allApproved = hasSubmissions && submissions.every(s => s.status === 'approved');
                    const isUploading = uploadingReqId === gstReq.id;
                    return (
                      <div data-testid="gst-upload-section">
                        {/* Uploaded files */}
                        {hasSubmissions && (
                          <div className="space-y-2 mb-4">
                            <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-2">Uploaded Files ({submissions.length})</p>
                            {submissions.map((sub) => (
                              <div key={sub.id} className="flex items-center justify-between bg-[#152219]/50 rounded-lg px-3 py-2.5" data-testid={`submission-${sub.id}`}>
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <FileText className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                                  {sub.file_url ? (
                                    <a href={getFileUrl(sub.file_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-400 hover:text-cyan-300 truncate">
                                      {sub.value || sub.original_filename || 'View file'}
                                    </a>
                                  ) : (
                                    <p className="text-sm text-slate-300 truncate">{sub.value}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    sub.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                                    sub.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                    'bg-amber-500/20 text-amber-400'
                                  }`}>{sub.status}</span>
                                  {canEditDocuments() && sub.status !== 'approved' && (
                                    <button onClick={() => handleDeleteSubmission(sub.id)} className="text-slate-500 hover:text-red-400 transition-colors p-1" data-testid={`delete-submission-${sub.id}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Upload button */}
                        {canEditDocuments() && !allApproved && (
                          <div>
                            <input type="file" accept=".pdf,.xlsx,.xls,.csv,.xml,.jpg,.png,.jpeg,.bmp,.doc,.docx" ref={el => fileInputRef.current[gstReq.id] = el} onChange={(e) => { handleFileUpload(gstReq.id, e.target.files?.[0]); e.target.value = ''; }} className="hidden" />
                            <Button onClick={() => fileInputRef.current[gstReq.id]?.click()} disabled={isUploading} className="bg-indigo-600 hover:bg-indigo-700 h-11 px-6" data-testid="gst-upload-btn">
                              {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                              {hasSubmissions ? 'Upload Another File' : 'Upload Sales Data'}
                            </Button>
                            <p className="text-xs text-slate-500 mt-2">Accepts PDF, Excel, CSV, Images (JPG/PNG), Word (.docx) — multi-page PDFs supported</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Non-GST: Standard requirements */}
                  {!isGSTProduct && order.requirements?.map((req) => {
                    const submissions = getSubmissions(req.id);
                    const hasSubmissions = submissions.length > 0;
                    const allApproved = hasSubmissions && submissions.every(s => s.status === 'approved');
                    const hasRejected = submissions.some(s => s.status === 'rejected');
                    const isUploading = uploadingReqId === req.id;
                    const isSubmittingText = submittingText === req.id;
                    
                    return (
                      <div key={req.id} className="bg-[#152219]/50 rounded-lg p-4" data-testid={`requirement-${req.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3">
                            {hasSubmissions ? (
                              <CheckCircle className={`w-5 h-5 flex-shrink-0 ${allApproved ? 'text-emerald-400' : hasRejected ? 'text-red-400' : 'text-amber-400'}`} />
                            ) : (
                              <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${req.is_required === false ? 'border-slate-500' : 'border-amber-500'}`} />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-white">{req.label}</p>
                                {req.is_required === false && (
                                  <span className="text-[10px] uppercase tracking-wider bg-[#182A22]/60 text-slate-300 px-1.5 py-0.5 rounded" data-testid={`optional-badge-${req.id}`}>
                                    Optional
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-400">
                                {req.type === 'file_upload' ? `File Upload${hasSubmissions ? ` (${submissions.length} file${submissions.length > 1 ? 's' : ''})` : ''}` : 'Text Input'}
                              </p>
                              {req.is_required === false && (
                                <p className="text-xs text-slate-500 italic mt-1" data-testid={`optional-hint-${req.id}`}>
                                  This is optional. If not applicable to your filing, you can leave it blank and submit for review.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Show all uploaded files */}
                        {hasSubmissions && (
                          <div className="ml-8 mt-2 space-y-2">
                            {submissions.map((sub) => (
                              <div key={sub.id} className="flex items-center justify-between bg-[#0f1611]/60 rounded-md px-3 py-2" data-testid={`submission-${sub.id}`}>
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  {sub.file_url ? (
                                    <a href={getFileUrl(sub.file_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1 truncate">
                                      <Download className="w-3.5 h-3.5 flex-shrink-0" /> 
                                      <span className="truncate">{sub.value || sub.original_filename || 'View file'}</span>
                                    </a>
                                  ) : (
                                    <p className="text-sm text-slate-300 truncate">{sub.value}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    sub.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                                    sub.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                    'bg-amber-500/20 text-amber-400'
                                  }`}>
                                    {sub.status}
                                  </span>
                                  {canEditDocuments() && sub.status !== 'approved' && (
                                    <button
                                      onClick={() => handleDeleteSubmission(sub.id)}
                                      className="text-slate-500 hover:text-red-400 transition-colors p-1"
                                      title="Remove file"
                                      data-testid={`delete-submission-${sub.id}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Upload / Input area — always show for file_upload if editable, show for text only if no submission */}
                        {canEditDocuments() && (
                          <div className="ml-8 mt-3">
                            {req.type === 'file_upload' ? (
                              !allApproved && (
                                <>
                                  <input type="file" ref={el => fileInputRef.current[req.id] = el} onChange={(e) => { handleFileUpload(req.id, e.target.files?.[0]); e.target.value = ''; }} className="hidden" />
                                  <Button size="sm" onClick={() => fileInputRef.current[req.id]?.click()} disabled={isUploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`upload-btn-${req.id}`}>
                                    {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                                    {hasSubmissions ? 'Add Another File' : 'Upload File'}
                                  </Button>
                                </>
                              )
                            ) : (
                              (!hasSubmissions || submissions.some(s => s.status === 'rejected')) && (
                                <div className="flex gap-2">
                                  <Input 
                                    placeholder={`Enter ${req.label}`}
                                    value={textInputs[req.id] || ''}
                                    onChange={(e) => setTextInputs(prev => ({...prev, [req.id]: e.target.value}))}
                                    className="bg-[#0f1611] border-emerald-800/40 text-white flex-1"
                                  />
                                  <Button size="sm" onClick={() => handleTextSubmit(req.id)} disabled={isSubmittingText || !textInputs[req.id]?.trim()} className="bg-emerald-600 hover:bg-emerald-700">
                                    {isSubmittingText ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
                                  </Button>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Signature Documents - shown with a clear header */}
                  {/* For filing products: Show "Filing Documents" with approve/reject flow */}
                  {/* For non-filing products: Show traditional signature upload flow */}
                  {order.signature_documents?.length > 0 ? (
                    <div className="pt-4 border-t border-emerald-800/40 mt-4">
                      <h3 className="text-sm font-medium text-amber-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <PenTool className="w-4 h-4" />
                        {isFilingProduct ? 'Filing Documents for Client Approval' : 'Documents Requiring Your Signature'} ({order.signature_documents.length})
                      </h3>
                      <div className="space-y-3">
                        {order.signature_documents.map((doc) => {
                          const isUploading = uploadingSignedDocId === doc.id;
                          
                          return (
                            <div key={doc.id} className="bg-[#152219]/50 rounded-lg p-4 border border-amber-500/30">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  {doc.status === 'approved' ? (
                                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                                  ) : doc.signed_url ? (
                                    <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                                  ) : (
                                    <div className="w-5 h-5 rounded-full border-2 border-amber-500 flex-shrink-0" />
                                  )}
                                  <div>
                                    <p className="font-medium text-white">{doc.document_name}</p>
                                    <p className="text-xs text-amber-400">
                                      {isFilingProduct ? 'Review and approve this document' : 'Download, sign, and upload back'}
                                    </p>
                                  </div>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs ${
                                  doc.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 
                                  doc.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 
                                  doc.signed_url ? 'bg-amber-500/20 text-amber-400' : 
                                  'bg-slate-500/20 text-slate-400'
                                }`}>
                                  {doc.status === 'approved' ? 'Approved' : doc.status === 'rejected' ? 'Rejected' : doc.signed_url ? 'Under Review' : isFilingProduct ? 'Pending Review' : 'Pending Signature'}
                                </span>
                              </div>
                              
                              <div className="ml-8 mt-3 flex items-center gap-3 flex-wrap">
                                <a href={getFileUrl(doc.document_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1 px-3 py-1.5 bg-cyan-500/10 rounded-lg">
                                  <Download className="w-4 h-4" /> {isFilingProduct ? 'Download Document' : 'Download to Sign'}
                                </a>
                                {doc.signed_url && (
                                  <a href={getFileUrl(doc.signed_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1 px-3 py-1.5 bg-emerald-500/10 rounded-lg">
                                    <Eye className="w-4 h-4" /> View Signed Copy
                                  </a>
                                )}
                              </div>
                              
                              {/* For filing products: Show Approve/Reject buttons with remark form */}
                              {isFilingProduct && canEditDocuments() && !doc.signed_url && doc.status !== 'approved' && (
                                <div className="ml-8 mt-4 space-y-3">
                                  {/* Initial buttons to start approve/reject flow */}
                                  {!docActionForm[doc.id]?.showForm && (
                                    <>
                                      <p className="text-xs text-slate-400">Review the document above and indicate your approval or rejection below:</p>
                                      <div className="flex gap-2">
                                        <Button 
                                          size="sm" 
                                          className="bg-emerald-600 hover:bg-emerald-700"
                                          onClick={() => setDocActionForm(prev => ({ ...prev, [doc.id]: { showForm: 'approve', remark: '' } }))}
                                        >
                                          <CheckCircle className="w-4 h-4 mr-1" />
                                          Approve Document
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                          onClick={() => setDocActionForm(prev => ({ ...prev, [doc.id]: { showForm: 'reject', remark: '' } }))}
                                        >
                                          <XCircle className="w-4 h-4 mr-1" />
                                          Request Changes
                                        </Button>
                                      </div>
                                    </>
                                  )}

                                  {/* Approve confirmation form */}
                                  {docActionForm[doc.id]?.showForm === 'approve' && (
                                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 space-y-3">
                                      <p className="text-emerald-400 text-sm font-medium">Confirm Approval</p>
                                      <textarea
                                        value={docActionForm[doc.id]?.remark || ''}
                                        onChange={(e) => setDocActionForm(prev => ({ 
                                          ...prev, 
                                          [doc.id]: { ...prev[doc.id], remark: e.target.value } 
                                        }))}
                                        placeholder="Add a remark (optional)..."
                                        rows={2}
                                        className="w-full bg-[#0b1310] border border-emerald-800/40 text-white rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-500 placeholder:text-slate-500"
                                      />
                                      
                                      {/* Legal Declaration Checkbox */}
                                      <label className="flex items-start gap-3 cursor-pointer group bg-[#080b09]/50 rounded-lg p-3 border border-emerald-900/30">
                                        <input
                                          type="checkbox"
                                          checked={docActionForm[doc.id]?.declaration || false}
                                          onChange={(e) => setDocActionForm(prev => ({ 
                                            ...prev, 
                                            [doc.id]: { ...prev[doc.id], declaration: e.target.checked } 
                                          }))}
                                          className="mt-0.5 w-4 h-4 rounded border-emerald-800/40 bg-[#0f1611] text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0"
                                        />
                                        <span className="text-xs text-slate-300 group-hover:text-white leading-relaxed">
                                          I confirm that I have reviewed the above document(s) and the information is accurate. I authorise Snigdha & Associates to proceed with the filing on my behalf.
                                        </span>
                                      </label>

                                      <div className="flex gap-2">
                                        <Button 
                                          size="sm" 
                                          className="bg-emerald-600 hover:bg-emerald-700"
                                          onClick={() => handleSignedDocUpload(doc.id, null, 'approve', docActionForm[doc.id]?.remark || '')}
                                          disabled={isUploading || !docActionForm[doc.id]?.declaration}
                                        >
                                          {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                                          Confirm Approval
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          className="border-emerald-800/40 text-slate-300"
                                          onClick={() => setDocActionForm(prev => ({ ...prev, [doc.id]: { showForm: null, remark: '', declaration: false } }))}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}

                                  {/* Reject/Request Changes form */}
                                  {docActionForm[doc.id]?.showForm === 'reject' && (
                                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 space-y-3">
                                      <p className="text-red-400 text-sm font-medium">Request Changes</p>
                                      <textarea
                                        value={docActionForm[doc.id]?.remark || ''}
                                        onChange={(e) => setDocActionForm(prev => ({ 
                                          ...prev, 
                                          [doc.id]: { ...prev[doc.id], remark: e.target.value } 
                                        }))}
                                        placeholder="Describe what changes are needed (required)..."
                                        rows={3}
                                        className="w-full bg-[#0b1310] border border-emerald-800/40 text-white rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-red-500 placeholder:text-slate-500"
                                      />
                                      <div className="flex gap-2">
                                        <Button 
                                          size="sm" 
                                          className="bg-red-600 hover:bg-red-700"
                                          onClick={() => handleSignedDocUpload(doc.id, null, 'reject', docActionForm[doc.id]?.remark || '')}
                                          disabled={isUploading || !docActionForm[doc.id]?.remark?.trim()}
                                        >
                                          {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                                          Submit Request
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          className="border-emerald-800/40 text-slate-300"
                                          onClick={() => setDocActionForm(prev => ({ ...prev, [doc.id]: { showForm: null, remark: '' } }))}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Show client's remark if they have acted */}
                              {isFilingProduct && doc.client_remark && (
                                <div className={`ml-8 mt-3 p-3 rounded-lg border ${
                                  doc.client_action === 'approve' 
                                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                                    : 'bg-amber-500/10 border-amber-500/30'
                                }`}>
                                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Your Remark</p>
                                  <p className="text-sm text-slate-300">{doc.client_remark}</p>
                                </div>
                              )}

                              {/* For non-filing products: Show upload signed copy button */}
                              {!isFilingProduct && canEditDocuments() && (!doc.signed_url || doc.status === 'rejected') && (
                                <div className="ml-8 mt-3">
                                  <input type="file" ref={el => signedFileInputRef.current[doc.id] = el} onChange={(e) => handleSignedDocUpload(doc.id, e.target.files?.[0])} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Button size="sm" onClick={() => signedFileInputRef.current[doc.id]?.click()} disabled={isUploading} className={doc.status === 'rejected' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'} data-testid={`signed-upload-btn-${doc.id}`}>
                                      {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                                      {doc.status === 'rejected' ? 'Re-upload' : 'Upload Signed Copy'}
                                    </Button>
                                    {doc.status === 'rejected' && doc.admin_comment && (
                                      <Button size="sm" variant="outline" onClick={() => setRejectionPopup({ show: true, doc })} className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`show-rejection-btn-${doc.id}`}>
                                        <AlertCircle className="w-4 h-4 mr-1" /> View Rejection Details
                                      </Button>
                                    )}
                                  </div>
                                  {doc.admin_comment && doc.status === 'rejected' && (
                                    <p className="text-xs text-red-400 mt-2">Rejection reason: {doc.admin_comment}</p>
                                  )}
                                </div>
                              )}

                              {doc.admin_comment && doc.status === 'rejected' && isFilingProduct && (
                                <div className="ml-8 mt-2 p-2 bg-red-500/10 rounded border border-red-500/30">
                                  <p className="text-xs text-red-400">Admin feedback: {doc.admin_comment}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Signature Review Status Message */}
                      {order.order?.status === 'signature_review' && (
                        <div className="mt-4 bg-indigo-500/10 border border-indigo-500/40 rounded-lg p-4 text-center">
                          <CheckCircle className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
                          <p className="text-indigo-400 font-medium">{isFilingProduct ? 'Documents Submitted for Review' : 'Signed Documents Submitted for Review'}</p>
                          <p className="text-sm text-slate-400 mt-1">We have received your request. We will confirm as soon as possible via email.</p>
                        </div>
                      )}
                    </div>
                  ) : ['signature_pending', 'signature_review'].includes(order.order?.status) && (
                    <div className="pt-4 border-t border-emerald-800/40 mt-4">
                      <h3 className="text-sm font-medium text-amber-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <PenTool className="w-4 h-4" />
                        Signature Documents
                      </h3>
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
                        <PenTool className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                        <p className="text-amber-400 font-medium">Awaiting Documents</p>
                        <p className="text-sm text-slate-400 mt-1">Our team is preparing documents for your signature. You'll be notified when they're ready.</p>
                      </div>
                    </div>
                  )}

                  {/* Submit for Review Button - Hide for filing products */}
                  {!isFilingProduct && canEditDocuments() && totalDocsCount > 0 && (
                    <div className="pt-4 border-t border-emerald-900/40">
                      {['under_review', 'signature_review'].includes(order.order?.status) ? (
                        <div className="bg-indigo-500/10 border border-indigo-500/40 rounded-lg p-4 text-center">
                          <CheckCircle className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
                          <p className="text-indigo-400 font-medium">Documents Submitted for Review</p>
                          <p className="text-sm text-slate-400 mt-1">We have received your request. We will confirm as soon as possible via email.</p>
                        </div>
                      ) : order.order?.documents_submitted_at ? (
                        /* Documents were submitted but status may have changed - still show disabled state */
                        <div className="bg-indigo-500/10 border border-indigo-500/40 rounded-lg p-4 text-center">
                          <CheckCircle className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
                          <p className="text-indigo-400 font-medium">Documents Previously Submitted</p>
                          <p className="text-sm text-slate-400 mt-1">Your documents are being processed.</p>
                        </div>
                      ) : (
                        <>
                          <Button 
                            onClick={handleSubmitForReview}
                            disabled={submittingForReview || !allDocumentsReady()}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {submittingForReview ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                            ) : (
                              <><Send className="w-4 h-4 mr-2" /> Submit All Documents for Review</>
                            )}
                          </Button>
                          {!allDocumentsReady() && (
                            <p className="text-xs text-slate-400 text-center mt-2">Complete all <span className="text-amber-400 font-medium">required</span> documents before submitting. Optional items can be left blank.</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}


            {/* GST Filing Controls — Admin Only */}
            {isAdmin && isGSTProduct && (
              <div className="bg-[#0f1611] rounded-xl border border-indigo-500/30 overflow-hidden" data-testid="gst-admin-section">
                <div className="p-5 border-b border-emerald-900/40 bg-gradient-to-r from-indigo-500/10 to-transparent">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-white" data-testid="gst-admin-title">GST Filing Agent</h2>
                      <p className="text-sm text-slate-400">
                        {order?.order?.filing_period_label || 'This period'} &bull; {order?.order?.gstin || order?.company?.gstin || '—'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  {/* Status display */}
                  {order?.order?.gst_filing_status && (
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-xs text-slate-500">Current Status:</span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        order.order.gst_filing_status === 'client_confirmed' ? 'bg-emerald-500/15 text-emerald-400' :
                        order.order.gst_filing_status === 'pending_client_confirmation' ? 'bg-amber-500/15 text-amber-400' :
                        order.order.gst_filing_status === 'sheet_ready' ? 'bg-blue-500/15 text-blue-400' :
                        'bg-[#182A22]/20 text-slate-400'
                      }`} data-testid="gst-filing-status">
                        {order.order.gst_filing_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                  )}

                  {/* Prepare button or status */}
                  {gstPreparing ? (
                    <div className="flex items-center gap-3 py-4" data-testid="gst-preparing-indicator">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                      <div>
                        <p className="text-white font-medium">Preparing GST Sheet...</p>
                        <p className="text-slate-400 text-sm">Analysing uploaded documents. This may take 30-60 seconds.</p>
                      </div>
                    </div>
                  ) : gstSheetStatus === 'completed' ? (
                    <div className="flex items-center gap-3 py-2" data-testid="gst-sheet-complete">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <p className="text-emerald-400 text-sm font-medium">Sheet prepared successfully. Open Filing Boards for full review.</p>
                    </div>
                  ) : gstSheetStatus === 'failed' ? (
                    <div className="flex items-center gap-3 py-2" data-testid="gst-sheet-failed">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <p className="text-red-400 text-sm">Sheet preparation failed. Try again or check Filing Boards.</p>
                    </div>
                  ) : (
                    <Button
                      onClick={handlePrepareGstSheet}
                      className="bg-indigo-600 hover:bg-indigo-700 h-11 px-6"
                      data-testid="prepare-gst-sheet-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" /> Prepare GST Sheet
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* GST Sheet Review — Client View */}
            {!isAdmin && isGSTProduct && order?.order?.gst_filing_status === 'pending_client_confirmation' && order?.order?.gst_confirmed_sheet && (
              <div className="bg-[#0f1611] rounded-xl border border-emerald-900/40 overflow-hidden" data-testid="gst-sheet-review-section">
                {/* Header */}
                <div className="p-5 border-b border-emerald-900/40 bg-gradient-to-r from-indigo-500/8 to-transparent">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FileCheck className="w-5 h-5 text-indigo-400" />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-white">GSTR-1 Summary Ready</h2>
                        <p className="text-sm text-slate-400 mt-0.5">
                          {order?.order?.filing_period_label || 'This period'} — Please review and confirm
                        </p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-xs bg-amber-500/15 text-amber-400 border border-amber-500/20 font-medium flex-shrink-0">
                      Awaiting Confirmation
                    </span>
                  </div>
                </div>

                <div className="p-5 space-y-5">
                  {/* Tax Summary Cards */}
                  {(() => {
                    const sheet = order.order.gst_confirmed_sheet;
                    const s = sheet?.summary;
                    if (!s) return null;
                    const items = [
                      { label: 'Taxable Value', value: s.total_taxable_value, color: 'text-white' },
                      { label: 'CGST', value: s.total_cgst, color: 'text-emerald-400' },
                      { label: 'SGST', value: s.total_sgst, color: 'text-cyan-400' },
                      { label: 'IGST', value: s.total_igst, color: 'text-violet-400' },
                    ];
                    const totalTax = (s.total_cgst || 0) + (s.total_sgst || 0) + (s.total_igst || 0);
                    return (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {items.map((item) => (
                            <div key={item.label} className="bg-[#0b1310]/60 rounded-lg p-3 border border-emerald-900/40/40">
                              <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">{item.label}</p>
                              <p className={`text-lg font-bold mt-1 ${item.color}`}>
                                {typeof item.value === 'number' ? `₹${item.value.toLocaleString('en-IN')}` : '₹0'}
                              </p>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-between bg-[#0b1310]/40 rounded-lg px-4 py-2.5 border border-emerald-900/25">
                          <span className="text-sm text-slate-400">Total Tax Liability</span>
                          <span className="text-base font-bold text-white">₹{totalTax.toLocaleString('en-IN')}</span>
                        </div>
                        {s.total_invoices && (
                          <p className="text-xs text-slate-500 text-right">{s.total_invoices} invoice{s.total_invoices !== 1 ? 's' : ''} processed</p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Data Tables */}
                  {(() => {
                    const sheet = order.order.gst_confirmed_sheet;
                    const sections = [
                      { key: 'b2b', title: 'B2B Supplies', desc: 'Registered persons' },
                      { key: 'b2cl', title: 'B2CL', desc: 'Large B2C inter-state' },
                      { key: 'b2cs', title: 'B2CS', desc: 'Small B2C supplies' },
                      { key: 'hsn_summary', title: 'HSN Summary', desc: 'HSN-wise breakup' },
                      { key: 'exp', title: 'Exports', desc: 'Export supplies' },
                    ];
                    const activeSections = sections.filter(({ key }) => {
                      const d = sheet?.[key];
                      return d && (Array.isArray(d) ? d.length > 0 : true);
                    });
                    if (activeSections.length === 0) return null;
                    return (
                      <div className="space-y-2">
                        <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Detailed Breakup</p>
                        {activeSections.map(({ key, title, desc }) => {
                          const data = sheet[key];
                          const rows = Array.isArray(data) ? data : [data];
                          const cols = rows.length > 0 ? Object.keys(rows[0]).filter(c => !c.startsWith('_')) : [];
                          return (
                            <details key={key} className="group bg-[#080b09]/50 rounded-lg border border-emerald-900/25 overflow-hidden">
                              <summary className="px-4 py-3 text-sm cursor-pointer hover:bg-[#152219]/20 transition-colors flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ChevronDown className="w-3.5 h-3.5 text-slate-500 transition-transform group-open:rotate-180" />
                                  <span className="font-medium text-slate-200">{title}</span>
                                  <span className="text-slate-500">— {desc}</span>
                                </div>
                                <span className="text-xs text-slate-500 bg-[#152219]/80 px-2 py-0.5 rounded">{rows.length}</span>
                              </summary>
                              <div className="border-t border-emerald-900/25 overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-[#152219]/50">
                                      {cols.map(c => (
                                        <th key={c} className="text-left py-2 px-3 text-slate-500 font-medium whitespace-nowrap">
                                          {c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((row, i) => (
                                      <tr key={i} className="border-t border-emerald-900/30 hover:bg-[#152219]/10">
                                        {cols.map(c => (
                                          <td key={c} className="py-2 px-3 text-slate-300 whitespace-nowrap">
                                            {typeof row[c] === 'number' ? row[c].toLocaleString('en-IN') : row[c] != null ? String(row[c]) : '—'}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </details>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Action Buttons */}
                  <div className="pt-3 border-t border-emerald-900/40/40">
                    {/* Annual Subscription Banner */}
                    {(order?.order?.exempt_from_payment || order?.order?.payment_status === 'prepaid') && (
                      <div className="flex items-center gap-3 bg-indigo-500/8 border border-indigo-500/20 rounded-lg px-4 py-3 mb-3" data-testid="annual-subscription-banner">
                        <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-indigo-300">Included in your annual subscription</p>
                          {order?.company?.subscription_end_date && (
                            <p className="text-xs text-slate-500 mt-0.5">Active until {new Date(order.company.subscription_end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {!showGstCorrection ? (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                          onClick={handleGstConfirm}
                          disabled={gstConfirming}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-11 text-sm font-semibold"
                          data-testid="gst-confirm-btn"
                        >
                          {gstConfirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                          {(order?.order?.exempt_from_payment || order?.order?.payment_status === 'prepaid')
                            ? 'Confirm Data — Proceed with Filing'
                            : order?.order?.amount > 0
                              ? `Confirm & Pay ₹${Number(order.order.amount).toLocaleString('en-IN')}`
                              : 'Confirm Data — Proceed with Filing'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowGstCorrection(true)}
                          className="border-emerald-800/40 text-slate-300 hover:bg-[#152219]/50 h-11 text-sm"
                          data-testid="gst-correction-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2 text-red-400" /> Raise Correction
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3 bg-red-500/5 border border-red-500/15 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          <p className="text-sm font-medium text-red-400">Raise a Correction</p>
                        </div>
                        <p className="text-xs text-slate-400">Describe the issue — our team will review and send a corrected summary.</p>
                        <textarea
                          value={gstCorrectionText}
                          onChange={(e) => setGstCorrectionText(e.target.value)}
                          placeholder="E.g., Invoice #INV-023 amount should be ₹45,000 not ₹54,000..."
                          className="w-full bg-[#0b1310]/70 border border-emerald-900/40 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 focus:border-red-500/50 focus:outline-none resize-none"
                          rows={3}
                          data-testid="gst-correction-input"
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={handleGstCorrection}
                            disabled={gstCorrectionSubmitting || !gstCorrectionText.trim()}
                            className="bg-red-600 hover:bg-red-700 h-10"
                            data-testid="gst-correction-submit-btn"
                          >
                            {gstCorrectionSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                            Submit Correction
                          </Button>
                          <Button variant="outline" onClick={() => { setShowGstCorrection(false); setGstCorrectionText(''); }} className="border-emerald-900/40 text-slate-400 h-10">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-600 mt-2 text-center">
                      {(order?.order?.exempt_from_payment || order?.order?.payment_status === 'prepaid')
                        ? 'This filing is included in your annual subscription. By confirming, you authorize us to file GSTR-1 with this data.'
                        : order?.order?.amount > 0
                          ? `By confirming, you agree to pay ₹${Number(order.order.amount).toLocaleString('en-IN')} and authorize us to file GSTR-1 with this data.`
                          : 'By confirming, you authorize us to file GSTR-1 with this data on the GST portal.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* GST Confirmed — Client View */}
            {!isAdmin && isGSTProduct && order?.order?.gst_filing_status === 'client_confirmed' && (
              <div className="bg-[#0f1611] rounded-xl border border-emerald-500/20 overflow-hidden" data-testid="gst-confirmed-section">
                <div className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-white">GST Data Confirmed</h2>
                    <p className="text-sm text-slate-400 mt-0.5">You confirmed the GSTR-1 data. Filing is in progress.</p>
                  </div>
                </div>
              </div>
            )}


            {/* DSC Consent & Legal Declaration — Client View */}
            {!isAdmin && dscConsent && (
              <div className="bg-[#0f1611] rounded-xl border border-amber-500/30 overflow-hidden" data-testid="dsc-consent-client-section">
                <div className="p-4 border-b border-amber-500/20 bg-amber-500/5">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-400" />
                    DSC Consent Required
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                      dscConsent.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                      dscConsent.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>
                      {dscConsent.status === 'approved' ? 'Approved' :
                       dscConsent.status === 'rejected' ? 'Rejected' : 'Pending'}
                    </span>
                  </h2>
                </div>
                <div className="p-4 space-y-4">
                  {/* Order details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-400">Order:</span> <span className="text-white ml-1">{dscConsent.order_name}</span></div>
                    <div><span className="text-slate-400">Filing Purpose:</span> <span className="text-white ml-1">{dscConsent.filing_purpose}</span></div>
                    <div><span className="text-slate-400">Requested By:</span> <span className="text-white ml-1">{dscConsent.admin_name}</span></div>
                    <div><span className="text-slate-400">Requested On:</span> <span className="text-white ml-1">{new Date(dscConsent.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>
                  </div>

                  {/* Declaration */}
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                    <p className="text-sm text-amber-400 font-medium mb-2">Legal Declaration:</p>
                    <p className="text-sm text-slate-200 leading-relaxed italic">
                      &ldquo;{dscConsent.declaration_text}&rdquo;
                    </p>
                  </div>

                  {/* Action buttons — only for pending */}
                  {dscConsent.status === 'pending' && (
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="dsc-consent-checkbox">
                        <input
                          type="checkbox"
                          checked={dscCheckbox}
                          onChange={(e) => setDscCheckbox(e.target.checked)}
                          className="mt-0.5 w-4 h-4 rounded border-slate-500 bg-[#152219] text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm text-slate-300">
                          I confirm that I am providing consent for this specific filing.
                        </span>
                      </label>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleDscResponse('approve')}
                          disabled={!dscCheckbox || dscResponding}
                          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                          data-testid="dsc-approve-btn"
                        >
                          {dscResponding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                          Approve / I Agree
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleDscResponse('reject')}
                          disabled={dscResponding}
                          className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          data-testid="dsc-reject-btn"
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  )}

                  {dscConsent.status === 'approved' && (
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                      <p className="text-sm text-emerald-400 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" /> 
                        Consent approved on {new Date(dscConsent.consent_timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Acknowledgement Documents - shown when completed */}
            {order.order?.status === 'completed' && order.acknowledgement_documents?.length > 0 && (
              <div className="bg-[#0f1611] rounded-xl border border-emerald-900/40 overflow-hidden">
                <div className="p-4 border-b border-emerald-900/40">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-400" />
                    Acknowledgement Documents
                  </h2>
                  <p className="text-sm text-slate-400 mt-1">Download your completion certificates and acknowledgements</p>
                </div>
                
                <div className="p-4 space-y-3">
                  {order.acknowledgement_documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-[#152219]/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileCheck className="w-5 h-5 text-emerald-400" />
                        <span className="text-white">{doc.document_name}</span>
                      </div>
                      <a href={getFileUrl(doc.document_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                        <Download className="w-4 h-4" /> Download
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Service Progress & Billing Card */}
            <div className="bg-[#0f1611] rounded-xl border border-emerald-900/40 overflow-hidden">
              <div className="p-4 border-b border-emerald-900/40">
                <h2 className="text-lg font-semibold text-white">Service Progress</h2>
              </div>
              
              <div className="p-4">
                <div className="bg-[#152219]/50 rounded-xl p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white">
                        {order.order?.display_name || order.product?.name || 'Service'}
                        {order.order?.is_renewal && (
                          <span className="ml-2 text-sm px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">Renewal</span>
                        )}
                      </h3>
                      <p className="text-slate-400 text-sm">{order.product?.description}</p>
                      {/* Product Details — Category, Sub Category, Time to Complete */}
                      {(order.product?.category || order.product?.sub_category || order.product?.time_to_complete_days) && (
                        <div className="flex flex-wrap gap-2 mt-2" data-testid="product-details-tags">
                          {order.product?.category && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-[#182A22]/60 text-slate-300 border border-emerald-800/40">
                              <Tag className="w-3 h-3 text-slate-400" /> {order.product.category}
                            </span>
                          )}
                          {order.product?.sub_category && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-[#182A22]/60 text-slate-300 border border-emerald-800/40">
                              <FileText className="w-3 h-3 text-slate-400" /> {order.product.sub_category}
                            </span>
                          )}
                          {order.product?.time_to_complete_days && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-[#182A22]/60 text-slate-300 border border-emerald-800/40">
                              <Clock className="w-3 h-3 text-slate-400" /> {order.product.time_to_complete_days} days
                            </span>
                          )}
                        </div>
                      )}
                      {/* Inclusions & Exclusions — always visible */}
                      {(order.product?.inclusions?.length > 0 || order.product?.exclusions?.length > 0) && (
                        <div className="mt-3 space-y-2" data-testid="active-service-inclusions-exclusions">
                          {order.product?.inclusions?.length > 0 && (
                            <div className="space-y-1">
                              {order.product.inclusions.map((inc, idx) => (
                                <div key={idx} className="flex items-start gap-1.5">
                                  <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs text-slate-300">{inc.title}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {order.product?.exclusions?.length > 0 && (
                            <div className="space-y-1">
                              {order.product.exclusions.map((exc, idx) => (
                                <div key={idx} className="flex items-start gap-1.5">
                                  <XCircle className="w-3.5 h-3.5 text-red-400/70 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs text-slate-400 line-through decoration-red-400/40">{exc.title}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {order.order?.is_renewal && order.order?.parent_order_id && (
                        <p className="text-xs text-slate-500 mt-1">
                          Renewing order <button
                            onClick={() => navigate(`/orders/${order.order.parent_order_id}`)}
                            className="text-indigo-400 hover:underline"
                          >#{order.order.parent_order_id.slice(0, 8)}</button>
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-emerald-400">
                        {formatCurrency(order.order?.total_amount, getCurrencyInfo(order))}
                      </p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        order.order?.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                        order.order?.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {order.order?.status?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>

                  {/* Admin: Recurring Pause/Resume for this company */}
                  {isAdmin && order.order?.is_recurring && (
                    <div className="mb-4 flex items-center gap-3" data-testid="recurring-pause-section">
                      {recurringPauseStatus?.is_paused ? (
                        <>
                          <span className="text-xs px-2.5 py-1 rounded-full bg-orange-500/20 text-orange-400 font-medium border border-orange-500/30" data-testid="recurring-paused-badge">
                            Future orders paused
                          </span>
                          <span className="text-xs text-slate-500">
                            {recurringPauseStatus.pause?.pause_reason}
                          </span>
                          <Button size="sm" onClick={handleRecurringResume} disabled={pauseLoading} className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1 h-7" data-testid="recurring-resume-btn">
                            {pauseLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PlayCircle className="w-3 h-3 mr-1" />}
                            Resume
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={handleRecurringPause} disabled={pauseLoading} className="ml-auto border-orange-500/40 text-orange-400 hover:bg-orange-500/10 text-xs px-3 py-1 h-7" data-testid="recurring-pause-btn">
                          {pauseLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PauseCircle className="w-3 h-3 mr-1" />}
                          Pause Future Orders
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-400">Progress</span>
                      <span className="text-sm font-medium text-white">{progressPercent}%</span>
                    </div>
                    <div className="h-2 bg-[#182A22] rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }} />
                    </div>
                  </div>

                  {/* Progress Steps */}
                  <div className="mb-4">
                    <p className="text-sm text-slate-400 mb-2">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {PROGRESS_STEPS
                        .filter(s => s.key !== 'cancelled')
                        .filter(s => {
                          // Hide signature steps for filing products (they don't have physical signatures)
                          if (isFilingProduct && (s.key === 'signature_pending' || s.key === 'signature_review')) {
                            return false;
                          }
                          return true;
                        })
                        .map((step, idx) => {
                        const stepIndex = PROGRESS_STEPS.findIndex(s => s.key === step.key);
                        const isCompleted = stepIndex < currentStep;
                        const isCurrent = stepIndex === currentStep;
                        return (
                          <span 
                            key={step.key}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 ${
                              isCurrent 
                                ? 'bg-amber-500/20 text-amber-400 border-2 border-emerald-500' 
                                : isCompleted
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                  : 'bg-[#182A22]/60 text-slate-400 border border-emerald-800/30'
                            }`}
                          >
                            {isCompleted && <CheckCircle className="w-3 h-3" />}
                            {step.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Deadlines Section - Show if any deadlines exist */}
                  {(order.order?.govt_deadline || order.order?.statutory_deadline || order.order?.doc_collection_deadline || order.order?.info_submission_deadline) && (
                    <div className="mb-4 pt-4 border-t border-emerald-900/40">
                      <div className="flex items-center gap-2 mb-3">
                        <CalendarClock className="w-4 h-4 text-amber-400" />
                        <h4 className="text-sm font-semibold text-white">Important Deadlines</h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <DeadlineCard 
                          label="Information Submission"
                          date={order.order?.info_submission_deadline}
                          icon={FileText}
                          description="Submit all required information"
                        />
                        <DeadlineCard 
                          label="Document Collection"
                          date={order.order?.doc_collection_deadline}
                          icon={Upload}
                          description="Upload all required documents"
                        />
                        <DeadlineCard 
                          label="Government Due Date"
                          date={order.order?.govt_deadline || order.order?.statutory_deadline}
                          icon={AlertTriangle}
                          description="Final statutory deadline"
                        />
                      </div>
                    </div>
                  )}

                  {/* Service Duration — Duration-Based Products Only */}
                  {isDurationBased && isAdmin && (
                    <div className="mb-4 pt-4 border-t border-emerald-900/40" data-testid="service-duration-section">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4 text-indigo-400" />
                        <h4 className="text-sm font-semibold text-white">Service Duration</h4>
                      </div>
                      <div className="bg-[#152219]/50 rounded-lg p-4 border border-indigo-500/20 space-y-3">
                        <div className="flex items-center gap-3">
                          <Select
                            value={String(durationMonths || order?.order?.duration_months || '')}
                            onValueChange={(val) => setDurationMonths(parseInt(val))}
                          >
                            <SelectTrigger className="w-48 bg-[#0f1611] border-emerald-900/40 text-white" data-testid="duration-select">
                              <SelectValue placeholder="Select duration" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="12">12 months</SelectItem>
                              <SelectItem value="24">24 months</SelectItem>
                              <SelectItem value="36">36 months</SelectItem>
                              <SelectItem value="48">48 months</SelectItem>
                              <SelectItem value="60">60 months</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            onClick={handleSaveDuration}
                            disabled={savingDuration || !durationMonths}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            data-testid="save-duration-btn"
                          >
                            {savingDuration ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                            Save
                          </Button>
                        </div>

                        {/* Expiry Date Display */}
                        {order?.order?.expiry_date ? (
                          <div className="flex items-center gap-2 text-sm">
                            <CalendarClock className="w-4 h-4 text-amber-400" />
                            <span className="text-slate-400">Expiry Date:</span>
                            <span className="text-white font-medium">
                              {new Date(order.order.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                        ) : order?.order?.status === 'completed' && durationMonths ? (
                          <p className="text-xs text-amber-400">Expiry date will be calculated when duration is saved.</p>
                        ) : (
                          <p className="text-xs text-slate-500">Expiry date will be set on order completion.</p>
                        )}

                        {order?.order?.duration_months && (
                          <p className="text-xs text-slate-500">
                            Current duration: {order.order.duration_months} months
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Duration info for clients (read-only) */}
                  {isDurationBased && !isAdmin && order?.order?.duration_months && (
                    <div className="mb-4 pt-4 border-t border-emerald-900/40">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4 text-indigo-400" />
                        <h4 className="text-sm font-semibold text-white">Service Duration</h4>
                      </div>
                      <div className="bg-[#152219]/50 rounded-lg p-4 border border-emerald-900/30 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-slate-400">Duration:</span>
                          <span className="text-white font-medium">{order.order.duration_months} months</span>
                        </div>
                        {order?.order?.expiry_date && (
                          <div className="flex items-center gap-2 text-sm">
                            <CalendarClock className="w-4 h-4 text-amber-400" />
                            <span className="text-slate-400">Expiry Date:</span>
                            <span className="text-white font-medium">
                              {new Date(order.order.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ═══ BILLING & PAYMENT SECTION ═══ */}
                  {(() => {
                    const currency = getCurrencyInfo(order);
                    const payStatus = order.order?.payment_status;
                    const orderStatus = order.order?.status;
                    const isConfirmed = ['paid', 'mock_paid', 'confirmed', 'prepaid'].includes(payStatus);
                    const isProcessing = payStatus === 'payment_pending';
                    const isPending = !isConfirmed && !isProcessing;

                    // Multi-GSTIN billing info
                    const periodCombinedTotal = order.order?.period_combined_total;
                    const periodCombinedOrders = order.order?.period_combined_orders;
                    const gstinCountAtOrder = order.order?.gstin_count_at_order_time;
                    const unitPrice = order.order?.unit_price;
                    const filingPeriodKey = order.order?.filing_period_key;
                    const hasMultiGstin = periodCombinedOrders && periodCombinedOrders.length > 1;

                    return (
                    <div className="mt-4 pt-4 border-t border-emerald-900/40" data-testid="billing-payment-section">

                      {/* Multi-GSTIN Billing Summary */}
                      {hasMultiGstin && (
                        <div className="bg-indigo-500/8 border border-indigo-500/20 rounded-lg p-4 mb-4" data-testid="multi-gstin-billing-summary">
                          <div className="flex items-center gap-2 mb-3">
                            <IndianRupee className="w-4 h-4 text-indigo-400" />
                            <span className="text-sm font-semibold text-indigo-300">Period Billing Summary</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-slate-400">Period</span>
                              <p className="text-white font-medium">{filingPeriodKey || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-400">GSTINs</span>
                              <p className="text-white font-medium">{gstinCountAtOrder || periodCombinedOrders?.length || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-400">Per GSTIN</span>
                              <p className="text-white font-medium">{formatCurrency(unitPrice || order.order?.amount, currency)}</p>
                            </div>
                            <div>
                              <span className="text-slate-400">Combined Total</span>
                              <p className="text-emerald-400 font-bold text-base">{formatCurrency(periodCombinedTotal, currency)}</p>
                            </div>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-2">
                            {periodCombinedOrders?.length} GSTIN order(s) combined into one payment
                          </p>
                        </div>
                      )}

                      {/* === CONFIRMED: Payment already received === */}
                      {isConfirmed && orderStatus !== 'draft' && (
                        <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-4" data-testid="payment-confirmed-banner">
                          <div className="flex items-center gap-2 text-emerald-400 mb-2">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">Payment Already Received</span>
                          </div>
                          <p className="text-sm text-white">
                            Thank you! Your payment has been received. You can now proceed with document submission.
                          </p>
                          {hasMultiGstin && (
                            <p className="text-xs text-emerald-300/70 mt-1">
                              Combined payment of {formatCurrency(periodCombinedTotal, currency)} for {filingPeriodKey}
                            </p>
                          )}
                          {order.order?.payment_reference && (
                            <p className="text-xs text-slate-300 mt-2">
                              Reference: {order.order.payment_reference}
                            </p>
                          )}
                          {order.order?.payment_date && (
                            <p className="text-xs text-slate-400 mt-1">
                              Paid on: {new Date(order.order.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                      )}

                      {/* === PROCESSING: Payment being verified === */}
                      {isProcessing && (
                        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4" data-testid="payment-processing-banner">
                          <div className="flex items-center gap-2 text-amber-400 mb-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="font-medium">Payment Processing...</span>
                          </div>
                          <p className="text-sm text-white">
                            Your payment is being processed. We will confirm as soon as it is verified.
                          </p>
                          {hasMultiGstin && (
                            <p className="text-xs text-amber-300/70 mt-1">
                              Processing {formatCurrency(periodCombinedTotal, currency)} for {filingPeriodKey}
                            </p>
                          )}
                        </div>
                      )}

                      {/* === PENDING / DRAFT / PAYMENT_PENDING: Show Pay Button === */}
                      {!isConfirmed && ['draft', 'payment_pending', 'invoice_requested'].includes(orderStatus) && !order.order?.payment_confirmation_submitted_at && (
                        <>
                          {/* Pricing Breakdown */}
                          <div className="bg-[#152219]/50 rounded-lg p-4 mb-4 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Govt Fees (Statutory)</span>
                              <span className="text-white">{formatCurrency(order.order?.statutory_fees || order.product?.statutory_fees || 0, currency)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Platform Fees (Professional)</span>
                              <span className="text-white">{formatCurrency(order.order?.startease_fees || order.product?.startease_fees || 0, currency)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Subtotal</span>
                              <span className="text-white">{formatCurrency(order.order?.total_amount || 
                                 ((order.product?.startease_fees || 0) + (order.product?.statutory_fees || 0)), currency)}</span>
                            </div>
                            {order.order?.discount_percentage > 0 && (
                              <>
                                <div className="flex justify-between text-sm text-emerald-400">
                                  <span className="flex items-center gap-1">
                                    <Tag className="w-3 h-3" /> Discount ({order.order.discount_percentage}%)
                                  </span>
                                  <span>-{formatCurrency(order.order.discount_amount || 0, currency)}</span>
                                </div>
                                {order.order?.discount_reason && (
                                  <p className="text-xs text-slate-500 italic">Reason: {order.order.discount_reason}</p>
                                )}
                              </>
                            )}
                            <div className="border-t border-emerald-800/40 pt-2 mt-2 flex justify-between">
                              <span className="font-medium text-white">Total Payable</span>
                              <span className="font-bold text-emerald-400 text-lg">
                                {formatCurrency(order.order?.discount_percentage > 0 
                                   ? order.order?.final_amount 
                                   : (order.order?.total_amount || 
                                      ((order.product?.startease_fees || 0) + (order.product?.statutory_fees || 0))), currency)}
                              </span>
                            </div>
                          </div>

                          {/* Payment Due Alert for multi-GSTIN */}
                          {hasMultiGstin && (
                            <div className="bg-red-500/8 border border-red-500/20 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="payment-due-alert">
                              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                              <p className="text-sm text-red-300">
                                Payment Due: <span className="font-bold">{formatCurrency(periodCombinedTotal, currency)}</span> for {filingPeriodKey} ({periodCombinedOrders?.length} GSTINs)
                              </p>
                            </div>
                          )}

                          {!showBankDetails ? (
                            <>
                              <div className="bg-blue-900/30 border-blue-500/30 border rounded-lg p-4 mb-4">
                                <p className="text-sm text-blue-300">
                                  Ready to proceed? Choose your preferred payment method below.
                                </p>
                              </div>
                              <div className="space-y-3">
                                <Button 
                                  onClick={handleOnlinePayment} 
                                  disabled={processingOnlinePayment}
                                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 h-12"
                                  data-testid="pay-online-btn"
                                >
                                  {processingOnlinePayment ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                                  ) : (
                                    <><CreditCard className="w-4 h-4 mr-2" /> Pay Online (Card/UPI/NetBanking)</>
                                  )}
                                </Button>
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 border-t border-emerald-900/40"></div>
                                  <span className="text-xs text-slate-500">OR</span>
                                  <div className="flex-1 border-t border-emerald-900/40"></div>
                                </div>
                                <Button 
                                  onClick={handlePayment} 
                                  variant="outline"
                                  className="w-full border-emerald-800/40 text-slate-300 hover:bg-[#0f1611] h-12"
                                  data-testid="pay-bank-btn"
                                >
                                  <Building2 className="w-4 h-4 mr-2" /> Pay via Bank Transfer
                                </Button>
                              </div>
                            </>
                          ) : (
                            <div className="space-y-4">
                              <div className="bg-[#0f1611] rounded-lg p-4 border border-emerald-500/30">
                                <h4 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                                  <CreditCard className="w-4 h-4" /> Bank Transfer Details
                                </h4>
                                <div className="space-y-4">
                                  <div className="bg-[#080b09]/50 rounded-lg p-3">
                                    <p className="text-white font-medium mb-2">ICICI Bank</p>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                      <div>
                                        <span className="text-slate-400">Account Number:</span>
                                        <p className="text-white font-mono">429305500142</p>
                                      </div>
                                      <div>
                                        <span className="text-slate-400">IFSC Code:</span>
                                        <p className="text-white font-mono">ICIC0004293</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="bg-[#080b09]/50 rounded-lg p-3">
                                    <p className="text-white font-medium mb-2">HDFC Bank</p>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                      <div>
                                        <span className="text-slate-400">Account Number:</span>
                                        <p className="text-white font-mono">99998718888687</p>
                                      </div>
                                      <div>
                                        <span className="text-slate-400">IFSC Code:</span>
                                        <p className="text-white font-mono">HDFC0006228</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-xs text-slate-400 mt-3">
                                  Please transfer the exact amount shown above to any of these accounts.
                                </p>
                              </div>
                              <div className="flex gap-3">
                                <Button 
                                  variant="outline" 
                                  onClick={() => setShowBankDetails(false)}
                                  className="flex-1 border-emerald-800/40 text-slate-300 hover:bg-[#0f1611]"
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  onClick={handlePaymentConfirmation}
                                  disabled={submittingPaymentConfirmation}
                                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                >
                                  {submittingPaymentConfirmation ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                                  ) : (
                                    <><CheckCircle className="w-4 h-4 mr-2" /> I have Paid</>
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Payment Confirmation Submitted */}
                      {orderStatus === 'payment_confirmation_pending' && (
                        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-amber-400 mb-2">
                            <Clock className="w-5 h-5" />
                            <span className="font-medium">Payment Confirmation Pending</span>
                          </div>
                          <p className="text-sm text-white">
                            We have received your payment confirmation. Our team will verify and confirm once payment is received.
                          </p>
                        </div>
                      )}

                      {/* Invoice Requested */}
                      {orderStatus === 'invoice_requested' && (
                        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-amber-400 mb-2">
                            <Clock className="w-5 h-5" />
                            <span className="font-medium">Invoice Requested</span>
                          </div>
                          <p className="text-sm text-white">
                            Your invoice is being prepared. You&apos;ll receive it via email within 24 hours.
                          </p>
                        </div>
                      )}

                      {/* Payment Confirmation Submitted (bank transfer flow) */}
                      {orderStatus === 'payment_pending' && order.order?.payment_confirmation_submitted_at && (
                        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-amber-400 mb-2">
                            <Clock className="w-5 h-5" />
                            <span className="font-medium">Payment Confirmation Received</span>
                          </div>
                          <p className="text-sm text-white">
                            We have received your payment request. We will confirm as soon as possible via email.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Status Overview */}
          <div className="space-y-6">
            <div className="bg-[#0f1611] rounded-xl border border-emerald-900/40 p-5">
              <h3 className="text-lg font-semibold text-white mb-4">Status Overview</h3>
              
              <div className="space-y-2">
                {/* Step indicators - exclude cancelled */}
                {PROGRESS_STEPS
                  .filter(s => s.key !== 'cancelled')
                  .filter(s => {
                    // Hide signature steps for filing products
                    if (isFilingProduct && (s.key === 'signature_pending' || s.key === 'signature_review')) {
                      return false;
                    }
                    return true;
                  })
                  .map((step, idx) => {
                  const stepIndex = PROGRESS_STEPS.findIndex(s => s.key === step.key);
                  const isCompleted = stepIndex < currentStep;
                  const isCurrent = stepIndex === currentStep;
                  return (
                    <div key={step.key} className={`p-2.5 rounded-lg ${isCurrent ? 'bg-emerald-500/20 border border-emerald-500/40' : isCompleted ? 'bg-[#152219]/30' : 'bg-[#152219]/50'}`}>
                      <div className="flex items-center gap-2">
                        {isCompleted ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : isCurrent ? <Clock className="w-4 h-4 text-amber-400" /> : <Clock className="w-4 h-4 text-slate-500" />}
                        <span className={`text-sm ${isCurrent ? 'text-emerald-400 font-medium' : isCompleted ? 'text-slate-300' : 'text-slate-500'}`}>{step.label}</span>
                        <span className="ml-auto text-xs text-slate-500">{step.percent}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Documents progress */}
              {totalDocsCount > 0 && (
                <div className="mt-4 pt-4 border-t border-emerald-900/40">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Documents Progress</span>
                    <span className="text-white">{completedDocsCount}/{totalDocsCount}</span>
                  </div>
                  <div className="h-2 bg-[#152219] rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(completedDocsCount/totalDocsCount)*100}%` }} />
                  </div>
                </div>
              )}
            </div>

            {/* Government Filing Status Card - Client View (Read-Only) — shown on ALL orders unconditionally */}
            {(
              <div className="bg-[#0f1611] rounded-xl border border-emerald-900/40 p-5">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-indigo-400" />
                  Government Filing Status
                </h3>
                
                {(() => {
                  const status = order.order?.govt_filing_status || 'not_started';
                  const statusConfig = {
                    not_started: { label: 'Not Started', color: 'bg-slate-500/20 text-slate-300 border-emerald-800/30', icon: Clock, bgColor: 'bg-slate-500/10' },
                    submitted_to_govt: { label: 'Submitted to Government', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: FileText, bgColor: 'bg-blue-500/10' },
                    pending_approval: { label: 'Pending Approval', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock, bgColor: 'bg-amber-500/10' },
                    clarification_required: { label: 'Clarification Required', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: AlertCircle, bgColor: 'bg-orange-500/10' },
                    clarification_filed: { label: 'Clarification Filed', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: FileText, bgColor: 'bg-purple-500/10' },
                    approved: { label: 'Approved', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle, bgColor: 'bg-emerald-500/10' },
                    rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: X, bgColor: 'bg-red-500/10' },
                  };
                  const config = statusConfig[status] || statusConfig.not_started;
                  const StatusIcon = config.icon;
                  
                  return (
                    <div className="space-y-4">
                      {/* Status Badge */}
                      <div className={`p-4 rounded-lg border ${config.bgColor} ${config.color.replace('text-', 'border-').split(' ')[2]}`}>
                        <div className="flex items-center gap-3">
                          <StatusIcon className="w-6 h-6" />
                          <div>
                            <p className="font-semibold text-lg">{config.label}</p>
                            {order.order?.govt_filing_updated_at && (
                              <p className="text-xs opacity-75">
                                Last updated: {new Date(order.order.govt_filing_updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Application Number */}
                      {order.order?.govt_application_number && (
                        <div className="bg-[#080b09]/50 rounded-lg p-3 border border-emerald-900/30">
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Application/Reference Number</p>
                          <p className="text-white font-mono text-lg mt-1">{order.order.govt_application_number}</p>
                        </div>
                      )}

                      {/* Remark from Admin */}
                      {order.order?.govt_filing_remark && (
                        <div className={`p-4 rounded-lg border ${status === 'clarification_required' ? 'bg-orange-500/10 border-orange-500/30' : 'bg-[#080b09]/50 border-emerald-900/30'}`}>
                          <p className={`text-xs uppercase tracking-wider mb-2 ${status === 'clarification_required' ? 'text-orange-400' : 'text-slate-500'}`}>
                            {status === 'clarification_required' ? '⚠️ Action Required' : 'Remarks from Team'}
                          </p>
                          <p className="text-white text-sm">{order.order.govt_filing_remark}</p>
                        </div>
                      )}

                      {/* Status-specific messages */}
                      {status === 'clarification_required' && (
                        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                          <p className="text-orange-300 text-sm">
                            <strong>Please Note:</strong> The government has requested additional information. Please contact our team or check your email for details on what needs to be submitted.
                          </p>
                        </div>
                      )}

                      {status === 'approved' && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                          <p className="text-emerald-300 text-sm">
                            🎉 Congratulations! Your application has been approved by the government. Your certificates/documents will be available in the Acknowledgement section shortly.
                          </p>
                        </div>
                      )}

                      <p className="text-xs text-slate-500 pt-2 border-t border-emerald-900/25">
                        Effective Date: 01-01-2025 • For any queries, please contact our support team.
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add Director Modal */}
      {showAddDirector && (
        <AddDirectorModal 
          form={newDirectorForm}
          setForm={setNewDirectorForm}
          onSave={handleAddDirector}
          onClose={() => setShowAddDirector(false)}
          saving={addingDirector}
        />
      )}
      
      {/* Add Shareholder Modal */}
      {showAddShareholder && (
        <AddShareholderModal 
          form={newShareholderForm}
          setForm={setNewShareholderForm}
          onSave={handleAddShareholder}
          onClose={() => setShowAddShareholder(false)}
          saving={addingShareholder}
          directors={order.directors || []}
          company={order.company}
        />
      )}
      
      {/* Internal Notes & Activity Log — activity visible to all, notes admin only */}
      {isAnyUser && (
        <div className="max-w-6xl mx-auto px-3 sm:px-4 pb-6">
          <div className="bg-[#152219]/50 border border-emerald-900/30 rounded-xl overflow-hidden">

            {/* Tab bar */}
            <div className="flex border-b border-emerald-900/30">
              {/* Internal Notes tab — admin/super_admin only */}
              {isAdmin && (
                <button
                  data-testid="tab-internal-notes"
                  onClick={() => setAdminTab(adminTab === 'notes' ? null : 'notes')}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
                    adminTab === 'notes'
                      ? 'text-white border-b-2 border-indigo-500 bg-[#152219]/30'
                      : 'text-slate-400 hover:text-white hover:bg-[#152219]/20'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" /> Internal Notes
                </button>
              )}
              {/* Activity Log tab — all roles */}
              <button
                data-testid="tab-activity-log"
                onClick={() => setAdminTab(adminTab === 'activity' ? null : 'activity')}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
                  adminTab === 'activity'
                    ? 'text-white border-b-2 border-indigo-500 bg-[#152219]/30'
                    : 'text-slate-400 hover:text-white hover:bg-[#152219]/20'
                }`}
              >
                <Activity className="w-4 h-4" /> Activity Log
              </button>
            </div>

            {/* Internal Notes panel — admin only */}
            {isAdmin && adminTab === 'notes' && (
              <div className="p-6 space-y-5" data-testid="panel-internal-notes">

                {/* Compose form */}
                <div className="bg-[#080b09]/50 rounded-lg p-4 border border-emerald-900/25">
                  <textarea
                    data-testid="note-content-input"
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Write an internal note..."
                    rows={3}
                    className="w-full bg-transparent text-white text-sm placeholder:text-slate-500 resize-none outline-none"
                  />
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-emerald-900/30">
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        ref={noteFileRef}
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) { setNoteFile(f); setNoteFileName(f.name); }
                        }}
                      />
                      <button
                        data-testid="note-attach-btn"
                        onClick={() => noteFileRef.current?.click()}
                        className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
                      >
                        <Upload className="w-3 h-3" /> Attach file
                      </button>
                      {noteFileName && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {noteFileName}
                          <button
                            onClick={() => { setNoteFile(null); setNoteFileName(''); }}
                            className="ml-1 text-slate-500 hover:text-red-400"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={handlePostNote}
                      disabled={postingNote || !noteContent.trim()}
                      data-testid="post-note-btn"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4"
                    >
                      {postingNote && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                      Post Note
                    </Button>
                  </div>
                </div>

                {/* Notes list */}
                {loadingNotes ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                  </div>
                ) : notes.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-6" data-testid="no-notes-msg">
                    No internal notes yet
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="notes-list">
                    {notes.map((note) => (
                      <div key={note.id} className="bg-[#080b09]/50 rounded-lg p-4 border border-emerald-900/25" data-testid="note-card">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm font-medium">{note.posted_by_name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              note.posted_by_role === 'super_admin'
                                ? 'bg-purple-500/20 text-purple-400'
                                : 'bg-indigo-500/20 text-indigo-400'
                            }`}>
                              {note.posted_by_role === 'super_admin' ? 'Super Admin' : 'Admin'}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500">{toIST(note.created_at)}</span>
                        </div>
                        <p className="text-slate-300 text-sm whitespace-pre-wrap">{note.content}</p>
                        {note.file_url && (
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const downloadUrl = `${API_URL}/api/orders/${order.id}/notes/${note.id}/download`;
                                
                                // Fetch with auth header to get the redirect
                                const response = await fetch(downloadUrl, {
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  redirect: 'follow'
                                });
                                
                                if (!response.ok) {
                                  throw new Error('Download failed');
                                }
                                
                                // Get the blob and create download
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = note.file_name || 'attachment';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              } catch (error) {
                                console.error('Download error:', error);
                                toast.error('Failed to download attachment');
                              }
                            }}
                            className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer"
                            data-testid="note-file-link"
                          >
                            <Download className="w-3 h-3" /> {note.file_name || 'Download attachment'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Activity Log panel */}
            {adminTab === 'activity' && (
              <div className="p-6" data-testid="panel-activity-log">
                {loadingActivity ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                  </div>
                ) : activity.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-6" data-testid="no-activity-msg">
                    No activity recorded yet
                  </p>
                ) : (
                  <div className="relative" data-testid="activity-timeline">
                    {/* Vertical line */}
                    <div className="absolute left-3 top-3 bottom-3 w-px bg-[#152219]" />
                    <div className="space-y-6">
                      {activity.map((entry, idx) => (
                        <div key={entry.id} className="flex gap-4 relative" data-testid="activity-entry">
                          {/* Dot */}
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 mt-0.5 ${
                            idx === activity.length - 1 ? 'bg-indigo-600' : 'bg-[#152219]'
                          }`}>
                            <Clock className="w-3 h-3 text-white" />
                          </div>
                          <div className="flex-1 min-w-0 pb-1">
                            <p className="text-white text-sm">{entry.description}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-xs text-slate-500">{toIST(entry.created_at)}</span>
                              {entry.performed_by_name && (
                                <span className="text-xs text-slate-600">· {entry.performed_by_name}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* View Entity Detail Modal */}
      {viewingEntity && (
        <ViewEntityModal
          entity={viewingEntity}
          type={viewingEntityType}
          onClose={() => { setViewingEntity(null); setViewingEntityType(null); }}
          onEdit={handleEditFounder}
        />
      )}

      {/* Client-side Signature Rejection Popup */}
      {rejectionPopup.show && rejectionPopup.doc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="signature-rejection-popup" onClick={() => {
          const d = rejectionPopup.doc;
          const dismissKey = `sig_reject_seen:${d.id}:${(d.rejection_history || []).length}`;
          localStorage.setItem(dismissKey, '1');
          setRejectionPopup({ show: false, doc: null });
        }}>
          <div className="bg-[#0f1611] border-2 border-red-500/40 rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-full bg-red-500/20">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">Document Rejected</h3>
                <p className="text-sm text-slate-400 mt-0.5">{rejectionPopup.doc.document_name}</p>
              </div>
              <button
                onClick={() => {
                  const d = rejectionPopup.doc;
                  const dismissKey = `sig_reject_seen:${d.id}:${(d.rejection_history || []).length}`;
                  localStorage.setItem(dismissKey, '1');
                  setRejectionPopup({ show: false, doc: null });
                }}
                className="text-slate-400 hover:text-white"
                data-testid="rejection-popup-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-xs text-red-400 uppercase tracking-wide mb-1 font-medium">Reviewer Remark</p>
              <p className="text-sm text-slate-200 leading-relaxed" data-testid="rejection-popup-remark">
                {rejectionPopup.doc.admin_comment || 'Please re-upload the correctly signed document.'}
              </p>
            </div>
            {(rejectionPopup.doc.rejection_history || []).length > 1 && (
              <details className="mb-4 text-xs text-slate-500">
                <summary className="cursor-pointer hover:text-slate-300">View all prior feedback ({rejectionPopup.doc.rejection_history.length})</summary>
                <div className="mt-2 space-y-2">
                  {rejectionPopup.doc.rejection_history.map((entry, i) => (
                    <div key={i} className="p-2 rounded bg-slate-800/50 border-l-2 border-red-500/40">
                      <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                        <span>Attempt #{entry.attempt_number || i + 1}</span>
                        <span>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}</span>
                      </div>
                      <p className="text-xs text-slate-300">{entry.remark || '—'}</p>
                    </div>
                  ))}
                </div>
              </details>
            )}
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => {
                  const d = rejectionPopup.doc;
                  const dismissKey = `sig_reject_seen:${d.id}:${(d.rejection_history || []).length}`;
                  localStorage.setItem(dismissKey, '1');
                  setRejectionPopup({ show: false, doc: null });
                  // Trigger the file picker for this doc
                  setTimeout(() => {
                    signedFileInputRef.current[d.id]?.click();
                  }, 100);
                }}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="rejection-popup-reupload-btn"
              >
                <Upload className="w-4 h-4 mr-2" /> Re-upload Document
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  const d = rejectionPopup.doc;
                  const dismissKey = `sig_reject_seen:${d.id}:${(d.rejection_history || []).length}`;
                  localStorage.setItem(dismissKey, '1');
                  setRejectionPopup({ show: false, doc: null });
                }}
                className="w-full text-slate-400 hover:text-white"
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-components
const CompanyDisplay = ({ company }) => {
  return (
    <div className="space-y-4">
      {/* Basic Company Info */}
      <div className="grid grid-cols-2 gap-4">
        <div><p className="text-xs text-slate-400 uppercase mb-1">Description</p><p className="text-white">{company?.description || '-'}</p></div>
        <div><p className="text-xs text-slate-400 uppercase mb-1">Entity Type</p><p className="text-white">{ENTITY_TYPES.find(e => e.value === company?.entity_type)?.label || company?.entity_type?.replace(/_/g, ' ') || '-'}</p></div>
        <div><p className="text-xs text-slate-400 uppercase mb-1">State</p><p className="text-white">{company?.state || '-'}</p></div>
        <div><p className="text-xs text-slate-400 uppercase mb-1">CIN/LLPIN</p><p className="text-white">{company?.cin_llpin || '-'}</p></div>
        <div><p className="text-xs text-slate-400 uppercase mb-1">Authorized Capital</p><p className="text-white">{company?.capital_structure?.authorized_capital ? `₹${company.capital_structure.authorized_capital.toLocaleString()}` : '-'}</p></div>
        <div><p className="text-xs text-slate-400 uppercase mb-1">Paid Up Capital</p><p className="text-white">{company?.capital_structure?.paid_up_capital ? `₹${company.capital_structure.paid_up_capital.toLocaleString()}` : '-'}</p></div>
        <div><p className="text-xs text-slate-400 uppercase mb-1">Business Phone</p><p className="text-white">{company?.company_phone || '-'}</p></div>
        <div><p className="text-xs text-slate-400 uppercase mb-1">Business Email</p><p className="text-white">{company?.company_email || '-'}</p></div>
        <div className="col-span-2"><p className="text-xs text-slate-400 uppercase mb-1">Registered Address</p><p className="text-white">{company?.registered_address || '-'}</p></div>
      </div>
    </div>
  );
};

const CompanyEditForm = ({ form, setForm, onSave, onCancel, saving }) => {
  return (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Company Name</label><Input value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Entity Type</label>
        <Select value={form.entity_type} onValueChange={(v) => setForm({...form, entity_type: v})}>
          <SelectTrigger className="bg-[#152219] border-emerald-800/40 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>{ENTITY_TYPES.filter(t => !t.value.startsWith('us_')).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">State</label><Input value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">CIN/LLPIN</label><Input value={form.cin_llpin} onChange={(e) => setForm({...form, cin_llpin: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Authorized Capital (₹)</label><Input type="number" value={form.authorized_capital || ''} onChange={(e) => setForm({...form, authorized_capital: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" placeholder="e.g., 1000000" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Paid Up Capital (₹)</label><Input type="number" value={form.paid_up_capital || ''} onChange={(e) => setForm({...form, paid_up_capital: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" placeholder="e.g., 100000" /></div>
      <div className="col-span-2"><label className="text-xs text-slate-400 uppercase mb-1 block">Description</label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div className="col-span-2"><label className="text-xs text-slate-400 uppercase mb-1 block">Registered Address</label><Input value={form.registered_address} onChange={(e) => setForm({...form, registered_address: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Business Phone</label><Input value={form.company_phone} onChange={(e) => setForm({...form, company_phone: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Business Email</label><Input value={form.company_email} onChange={(e) => setForm({...form, company_email: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
    </div>
    
    <div className="flex justify-end gap-2 pt-2">
      <Button variant="ghost" onClick={onCancel} className="text-slate-400"><X className="w-4 h-4 mr-1" /> Cancel</Button>
      <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save</Button>
    </div>
  </div>
  );
};

const FounderDisplay = ({ founder, type, onEdit, shares, percent }) => (
  <div className="flex items-start justify-between">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#152219] text-emerald-400"><User className="w-5 h-5" /></div>
      <div>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <p className="font-medium text-white">{founder.full_name}</p>
          <span className="px-2 py-0.5 rounded text-xs bg-[#152219] text-slate-300">{type}</span>
          {founder.is_authorized_signatory && <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">Auth. Signatory</span>}
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <p className="text-slate-400"><Mail className="w-3 h-3 inline mr-1" /> {founder.email || '-'}</p>
          <p className="text-slate-400"><Phone className="w-3 h-3 inline mr-1" /> {founder.phone || '-'}</p>
          <p className="text-slate-400">DOB: {founder.date_of_birth || '-'}</p>
          <p className="text-slate-400">PAN: {founder.pan_number || '-'}</p>
          <p className="text-slate-400">Aadhaar: {founder.aadhaar_number || '-'}</p>
          <p className="text-slate-400">DSC: {founder.dsc_status === 'active' ? 'Yes' : 'No'}</p>
          <p className="text-slate-400">DSC Expiry: {founder.dsc_expiry || '-'}</p>
          <p className="text-slate-400">DIN: {founder.din_number || '-'}</p>
          <p className="text-slate-400">DIN Status: {founder.din_status || '-'}</p>
          {shares && <p className="text-slate-400">Shares: {shares} ({percent}%)</p>}
        </div>
      </div>
    </div>
    <button onClick={onEdit} className="text-emerald-400 hover:text-emerald-300"><Pencil className="w-4 h-4" /></button>
  </div>
);

const FounderEditForm = ({ form, setForm, onSave, onCancel, saving }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Full Name</label><Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Email</label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Phone</label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Date of Birth</label><Input type="date" value={form.date_of_birth || ''} onChange={(e) => setForm({...form, date_of_birth: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">PAN Number</label><Input value={form.pan_number} onChange={(e) => setForm({...form, pan_number: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">Aadhaar Number</label><Input value={form.aadhaar_number} onChange={(e) => setForm({...form, aadhaar_number: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">DSC Status</label>
        <Select value={form.dsc_status} onValueChange={(v) => setForm({...form, dsc_status: v})}>
          <SelectTrigger className="bg-[#152219] border-emerald-800/40 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>{DSC_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">DSC Expiry Date</label><Input type="date" value={form.dsc_expiry} onChange={(e) => setForm({...form, dsc_expiry: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">DIN Number</label><Input value={form.din_number} onChange={(e) => setForm({...form, din_number: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
      <div><label className="text-xs text-slate-400 uppercase mb-1 block">DIN Status</label>
        <Select value={form.din_status} onValueChange={(v) => setForm({...form, din_status: v})}>
          <SelectTrigger className="bg-[#152219] border-emerald-800/40 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>{DIN_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.is_authorized_signatory} onChange={(e) => setForm({...form, is_authorized_signatory: e.target.checked})} className="w-4 h-4 rounded" /><label className="text-sm text-slate-300">Authorized Signatory</label></div>
    </div>
    <div className="flex justify-end gap-2 pt-2">
      <Button variant="ghost" onClick={onCancel} className="text-slate-400"><X className="w-4 h-4 mr-1" /> Cancel</Button>
      <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save</Button>
    </div>
  </div>
);

const AddDirectorModal = ({ form, setForm, onSave, onClose, saving }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-[#0f1611] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="p-4 border-b border-emerald-900/40 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          Add New Director
        </h2>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">Full Name *</label><Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" placeholder="Enter full name" /></div>
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">Email *</label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" placeholder="Enter email" /></div>
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">Phone</label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">Date of Birth</label><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({...form, date_of_birth: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">PAN Number</label><Input value={form.pan_number} onChange={(e) => setForm({...form, pan_number: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">Aadhaar Number</label><Input value={form.aadhaar_number} onChange={(e) => setForm({...form, aadhaar_number: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">DIN Number</label><Input value={form.din_number} onChange={(e) => setForm({...form, din_number: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">DIN Status</label>
            <Select value={form.din_status} onValueChange={(v) => setForm({...form, din_status: v})}>
              <SelectTrigger className="bg-[#152219] border-emerald-800/40 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{DIN_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">DSC Status</label>
            <Select value={form.dsc_status} onValueChange={(v) => setForm({...form, dsc_status: v})}>
              <SelectTrigger className="bg-[#152219] border-emerald-800/40 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{DSC_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-slate-400 uppercase mb-1 block">DSC Expiry Date</label><Input type="date" value={form.dsc_expiry} onChange={(e) => setForm({...form, dsc_expiry: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
          <div className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.is_authorized_signatory} onChange={(e) => setForm({...form, is_authorized_signatory: e.target.checked})} className="w-4 h-4 rounded" /><label className="text-sm text-slate-300">Authorized Signatory</label></div>
          <div className="col-span-2"><label className="text-xs text-slate-400 uppercase mb-1 block">Address</label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="bg-[#152219] border-emerald-800/40 text-white" /></div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-emerald-900/40">
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Cancel</Button>
          <Button onClick={onSave} disabled={saving || !form.full_name || !form.email} className="bg-emerald-600 hover:bg-emerald-700">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Add Director</Button>
        </div>
      </div>
    </div>
  </div>
);

const AddShareholderModal = ({ form, setForm, onSave, onClose, saving, directors, company }) => {
  const totalShares = company?.capital_structure?.paid_up_capital / (company?.capital_structure?.share_face_value || 10) || 10000;
  const percentHolding = ((form.number_of_shares || 0) / totalShares * 100).toFixed(2);
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0f1611] rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-emerald-900/40 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Add Shareholder</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs text-slate-400 uppercase mb-1 block">Link to Director (Optional)</label>
            <Select 
              value={form.linked_director_id || 'manual'} 
              onValueChange={(v) => {
                if (v === 'manual') {
                  setForm({...form, linked_director_id: '', shareholder_name: '', shareholder_email: '', pan_number: ''});
                } else {
                  const director = directors.find(d => d.id === v);
                  setForm({
                    ...form, 
                    linked_director_id: v, 
                    shareholder_name: director?.full_name || '',
                    shareholder_email: director?.email || '',
                    pan_number: director?.pan_number || ''
                  });
                }
              }}
            >
              <SelectTrigger className="bg-[#152219] border-emerald-800/40 text-white">
                <SelectValue placeholder="Select or enter manually" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1611] border-emerald-900/40">
                <SelectItem value="manual" className="text-slate-200">Enter manually</SelectItem>
                {directors.map((d) => (
                  <SelectItem key={d.id} value={d.id} className="text-slate-200">
                    {d.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-xs text-slate-400 uppercase mb-1 block">Shareholder Name *</label>
            <Input 
              value={form.shareholder_name} 
              onChange={(e) => setForm({...form, shareholder_name: e.target.value})} 
              className="bg-[#152219] border-emerald-800/40 text-white" 
              placeholder="Enter name"
              disabled={!!form.linked_director_id}
            />
          </div>
          
          <div>
            <label className="text-xs text-slate-400 uppercase mb-1 block">Email</label>
            <Input 
              type="email"
              value={form.shareholder_email} 
              onChange={(e) => setForm({...form, shareholder_email: e.target.value})} 
              className="bg-[#152219] border-emerald-800/40 text-white" 
              placeholder="Enter email"
              disabled={!!form.linked_director_id}
            />
          </div>
          
          <div>
            <label className="text-xs text-slate-400 uppercase mb-1 block">PAN Number</label>
            <Input 
              value={form.pan_number || ''} 
              onChange={(e) => setForm({...form, pan_number: e.target.value.toUpperCase()})} 
              className="bg-[#152219] border-emerald-800/40 text-white" 
              placeholder="e.g., ABCDE1234F"
              maxLength={10}
            />
          </div>
          
          <div>
            <label className="text-xs text-slate-400 uppercase mb-1 block">Shareholder Type</label>
            <Select value={form.shareholder_type} onValueChange={(v) => setForm({...form, shareholder_type: v})}>
              <SelectTrigger className="bg-[#152219] border-emerald-800/40 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1611] border-emerald-900/40">
                <SelectItem value="individual" className="text-slate-200">Individual</SelectItem>
                <SelectItem value="body_corporate" className="text-slate-200">Body Corporate</SelectItem>
                <SelectItem value="huf" className="text-slate-200">HUF</SelectItem>
                <SelectItem value="trust" className="text-slate-200">Trust</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-xs text-slate-400 uppercase mb-1 block">Number of Shares *</label>
            <Input 
              type="number"
              value={form.number_of_shares} 
              onChange={(e) => setForm({...form, number_of_shares: Number(e.target.value)})} 
              className="bg-[#152219] border-emerald-800/40 text-white" 
              placeholder="e.g., 5000"
            />
            <p className="text-xs text-emerald-400 mt-1">
              = {percentHolding}% ownership (Total shares: {totalShares.toLocaleString()})
            </p>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t border-emerald-900/40">
            <Button variant="ghost" onClick={onClose} className="text-slate-400">Cancel</Button>
            <Button 
              onClick={onSave} 
              disabled={saving || !form.shareholder_name || !form.number_of_shares} 
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Add Shareholder
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// View Director/Shareholder Detail Modal
// Status Badge Component for ViewEntityModal
const EntityStatusBadge = ({ status, type }) => {
  const configs = {
    din: {
      active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
      not_applied: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Not Applied' },
      deactivated: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Deactivated' },
    },
    dsc: {
      active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
      applied: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Applied' },
      not_applied: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Not Applied' },
      expired: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Expired' },
    },
    signatory: {
      true: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Yes' },
      false: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'No' },
    },
    shareholder_type: {
      individual: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Individual' },
      company: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Company' },
      llp: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'LLP' },
      trust: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Trust' },
    }
  };
  
  const config = configs[type]?.[String(status)] || configs[type]?.['not_applied'] || { bg: 'bg-slate-500/20', text: 'text-slate-400', label: status || 'N/A' };
  
  return (
    <span className={`text-xs px-2 py-1 rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Display Field Component for ViewEntityModal
const EntityDisplayField = ({ label, value, mono = false, hideIfEmpty = true }) => {
  if (hideIfEmpty && !value) return null;
  
  return (
    <div className="bg-[#080b09]/50 rounded-lg p-3">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-sm text-white ${mono ? 'font-mono' : ''}`}>{value || '-'}</p>
    </div>
  );
};

const ViewEntityModal = ({ entity, type, onClose, onEdit }) => {
  if (!entity) return null;
  
  const isDirector = type === 'director';
  const name = isDirector ? entity.full_name : entity.shareholder_name;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#0f1611] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-emerald-900/40 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
              isDirector ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : 'bg-gradient-to-br from-purple-400 to-pink-500'
            }`}>
              {name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-slate-400 capitalize">{type}</p>
                {entity.is_authorized_signatory && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">Signatory</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2"><X className="w-5 h-5" /></button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Personal Information Section */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Personal Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <EntityDisplayField label={isDirector ? "Full Name" : "Name"} value={name} hideIfEmpty={false} />
              <EntityDisplayField label="Email" value={isDirector ? entity.email : entity.shareholder_email} hideIfEmpty={false} />
              <EntityDisplayField label="Phone" value={entity.phone} />
              <EntityDisplayField label="Date of Birth" value={entity.date_of_birth} />
              {isDirector && <EntityDisplayField label="Father's Name" value={entity.father_name} />}
              {entity.address && (
                <div className="col-span-2 bg-[#080b09]/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Address</p>
                  <p className="text-sm text-white">{entity.address}</p>
                </div>
              )}
            </div>
          </div>

          {/* Shareholder specific - Shareholding Details */}
          {!isDirector && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Shareholding Details</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#080b09]/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Type</p>
                  <EntityStatusBadge status={entity.shareholder_type} type="shareholder_type" />
                </div>
                <div className="bg-[#080b09]/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Shares</p>
                  <p className="text-lg font-bold text-white">{entity.number_of_shares?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-[#080b09]/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Holding</p>
                  <p className="text-lg font-bold text-emerald-400">{entity.percent_holding?.toFixed(1) || 0}%</p>
                </div>
              </div>
            </div>
          )}

          {/* Identity Details Section */}
          {(entity.pan_number || entity.aadhaar_number) && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Identity Details</h3>
              <div className="grid grid-cols-2 gap-3">
                <EntityDisplayField label="PAN Number" value={entity.pan_number} mono={true} />
                <EntityDisplayField label="Aadhaar Number" value={entity.aadhaar_number} mono={true} />
              </div>
            </div>
          )}
          
          {/* DIN & DSC Details Section */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">DIN & DSC Details</h3>
            <div className="grid grid-cols-2 gap-3">
              {/* DIN Number - Show actual value */}
              <div className="bg-[#080b09]/50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">DIN Number</p>
                <p className="text-sm text-white font-mono">{entity.din_number || '-'}</p>
              </div>
              {/* DIN Status with Badge */}
              <div className="bg-[#080b09]/50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">DIN Status</p>
                <EntityStatusBadge status={entity.din_status} type="din" />
              </div>
              {/* DSC Status with Badge */}
              <div className="bg-[#080b09]/50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">DSC Status</p>
                <EntityStatusBadge status={entity.dsc_status} type="dsc" />
              </div>
              {/* DSC Expiry */}
              <EntityDisplayField label="DSC Expiry" value={entity.dsc_expiry_date || entity.dsc_expiry} />
              {/* DSC Class & Provider */}
              <EntityDisplayField label="DSC Class" value={entity.dsc_class?.replace('_', ' ')} />
              <EntityDisplayField label="DSC Provider" value={entity.dsc_provider} />
              {/* Authorized Signatory */}
              <div className="col-span-2 bg-[#080b09]/50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Authorized Signatory</p>
                <EntityStatusBadge status={entity.is_authorized_signatory} type="signatory" />
              </div>
            </div>
          </div>

          {/* Director specific - Additional Information */}
          {isDirector && (entity.occupation || entity.nationality || entity.duration_of_stay) && (
            <div>
              <h3 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">Additional Information</h3>
              <div className="grid grid-cols-2 gap-3 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                <EntityDisplayField label="Occupation" value={entity.occupation} />
                <EntityDisplayField label="Nationality" value={entity.nationality} />
                {entity.duration_of_stay && (
                  <div className="col-span-2 bg-[#080b09]/50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Duration of Stay</p>
                    <p className="text-sm text-white">{entity.duration_of_stay?.replace(/_/g, ' ')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* V3 Credentials Section */}
          {(entity.v3_username || entity.v3_password || entity.v3_hint_question) && (
            <div>
              <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">V3 Credentials</h3>
              <div className="grid grid-cols-2 gap-3 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                <EntityDisplayField label="Username" value={entity.v3_username} mono={true} />
                <EntityDisplayField label="Password" value={entity.v3_password ? '••••••••' : null} />
                {entity.v3_hint_question && (
                  <div className="col-span-2 bg-[#080b09]/50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Security Hint</p>
                    <p className="text-sm text-white">{entity.v3_hint_question}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="p-4 border-t border-emerald-900/40 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Close</Button>
          <Button onClick={() => { onClose(); onEdit(entity, type); }} className="bg-emerald-600 hover:bg-emerald-700">
            <Pencil className="w-4 h-4 mr-1" /> Edit {type}
          </Button>
        </div>
      </div>
    </div>
  );
};

const OrderDetailPageWrapped = () => (
  <ErrorBoundary>
    <OrderDetailPage />
  </ErrorBoundary>
);

export default OrderDetailPageWrapped;
